import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, ProgressBar, Badge, Tooltip, OverlayTrigger, Modal, Table } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FileText, Upload, Sparkles, Plus, Info, Check, Edit3, Download, ChevronRight, Building2, Briefcase, MapPin, Calendar, RotateCcw, Clock, Users, Target, Zap, ArrowRight, CheckCircle2, AlertCircle, Database, Search, ExternalLink, Paperclip } from 'lucide-react';
import api, { resolveApiErrorMessage } from '../../services/axios';
import { useAuth } from '../../context/AuthContext';
import { AI_JD_OFF_MESSAGE, isAiJdEnabled } from '../../utils/entitlements';
import GlobalPagination from '../../components/ui/GlobalPagination';
import PositionsTable from '../../components/custom/PositionsTable';
import DatabasePage from './Database';

import { buildJdPreviewNavigationState } from '../../utils/jdPreviewNavigation';

import { normalizeJdExperienceZeroToEntryLevel } from '../../utils/jdNormalizeExperience';
import {
  formatJdPreviewHtml,
  normalizeJdForStorage,
  normalizeJdHtmlFromSource,
  JD_PREVIEW_INNER_CSS,
} from '../../utils/jdPreviewFormat';
import {
  sanitizeExperienceInput,
  validateExperienceField,
  isValidExperienceField,
} from '../../utils/validateExperience';
import {
  previewJobFamilyForTitle,
  resolveRoleKeyForPosition,
  autoFetchJobFamilyForPosition,
  ensureRoleSeatedInJobFamily,
  setPositionJobFamilyKey,
  getPositionJobFamilyKey,
  syncJobFamilySkillsToJd,
  ensurePositionSkillWeightages,
  isValidRoleKey,
  rememberLastCreatedRoleKey,
  setJobFamilyAiEnabled,
} from '../../data/jobFamilyMaster';
import { JobFamilyTripleSelect } from '../../components/custom/JobFamilyPanel';
import {
  sanitizeLocationInput,
  validateLocationField as validateWorkLocationField,
  isValidLocationField,
} from '../../utils/validateLocation';

const QUICK_INPUT_MAX_CHARS = 12000;
const JOB_TITLE_MAX_CHARS = 100;
const JOB_TITLE_WARN_CHARS = 80;
/** Exact copy required by product when title is too long for the field. */
const JOB_TITLE_EXCEED_MSG = 'Job Title cannot exceed 100 characters.';
const JOB_TITLE_REQUIRED_MSG = 'Please enter a Job Title.';
const GENERIC_INVALID_DETAILS_MSG = 'Please enter valid details.';

/** Ceipal/ATS titles often use Unicode en/em dashes (e.g. "Senior Engineer — BESS"); map to ASCII for validation and APIs. */
const normalizeImportedAtsLabel = (value) =>
  String(value ?? '')
    .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-')
    .replace(/\u00A0/g, ' ')
    .trim();

/** Job Title/Role must never exceed JOB_TITLE_MAX_CHARS (controlled state). */
const clampJobTitleRole = (value) => String(value ?? '').slice(0, JOB_TITLE_MAX_CHARS);

/** Normalize pasted unicode spaces so word counts match what users see. */
const normalizeRoleWhitespace = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * One-line "paragraph" job titles under 100 chars must still be rejected (same message as over 100).
 * Tiers catch verbose sentences without blocking short titles like "Senior Software Engineer".
 */
const isSentenceStyleRoleLine = (trimmed) => {
  const normalized = normalizeRoleWhitespace(trimmed);
  if (!normalized) return false;
  const words = normalized.split(' ').filter(Boolean);
  if (words.length >= 8 && normalized.length > 60) return true;
  if (words.length >= 7 && normalized.length > 72) return true;
  if (words.length >= 6 && normalized.length > 82) return true;
  return false;
};

const POSITION_NAME_MAX_CHARS = 120;
const CLIENT_NAME_MAX_CHARS = 120;
const JOB_CODE_MAX_CHARS = 40;

const JD_GENERATE_FAIL = 'Failed to generate job description. Please try again.';
const JD_GENERATE_FAIL_SHORT = 'Failed to generate job description.';

/** True when backend (or client) text is clearly a job-title length rejection. */
const isJobTitleLengthRejection = (text) => {
  if (typeof text !== 'string' || !text.trim()) return false;
  if (text.includes(JOB_TITLE_EXCEED_MSG)) return true;
  const lower = text.toLowerCase();
  return (
    lower.includes('100') &&
    (lower.includes('character') ||
      lower.includes('title') ||
      lower.includes('jobtitle') ||
      lower.includes('job title') ||
      lower.includes('exceed') ||
      lower.includes('max') ||
      lower.includes('larger') ||
      lower.includes('length') ||
      lower.includes('size'))
  );
};

/** Same red toast as other JD errors; use exact product line when the API rejected title length. */
const toastMessageForJdApiFailure = (serverMessage, fallback) => {
  if (isJobTitleLengthRejection(serverMessage)) return JOB_TITLE_EXCEED_MSG;
  if (typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage.trim();
  return fallback;
};

const toastMessageForGenerateJdCatch = (error) => {
  const d = error?.response?.data;
  const candidates = [
    typeof d === 'string' ? d : null,
    d?.message,
    d?.detail,
    d?.error,
    error?.message
  ].filter((x) => typeof x === 'string' && x.trim());
  for (const c of candidates) {
    if (isJobTitleLengthRejection(c)) return JOB_TITLE_EXCEED_MSG;
  }
  const first = candidates[0];
  if (first) return first.trim();
  return JD_GENERATE_FAIL;
};


const OpenPositions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Craft JD (jd_craft) off = AI generate/regenerate off; upload, edit and attach still work.
  const { entitlements, unrestricted } = useAuth();
  const aiJdOn = isAiJdEnabled(entitlements, { unrestricted });
  // Job family suggestions on this screen follow Craft JD's AI switch.
  setJobFamilyAiEnabled(aiJdOn);

  // 'list' = positions table landing, 'wizard' = 3-step JD creation/editing wizard
  const [view, setView] = useState('list');

  const [positionName, setPositionName] = useState('');
  const [clientName, setClientName] = useState('');
  const [selectedJobFamilyKey, setSelectedJobFamilyKey] = useState('');
  const [jobFamilyPreview, setJobFamilyPreview] = useState(null);
  const jobFamilyTouchedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [generatedJd, setGeneratedJd] = useState('');
  const [uploadedJd, setUploadedJd] = useState('');
  const [jdType, setJdType] = useState(''); // 'ai' or 'upload'
  const [showJdPreview, setShowJdPreview] = useState(false);
  const [positionId, setPositionId] = useState(null);
  const [activeTab, setActiveTab] = useState('structured');
  // Tracks how the current position was created.
  // Business rule: if a position was created manually, do not allow importing JD from Ceipal
  // (it can overwrite / conflict with manually entered position data).
  const [positionCreationSource, setPositionCreationSource] = useState(''); // 'manual' | 'ceipal' | ''

  // JD panel flow states (Change 2)
  // jdPanelOpen: user clicked "Create Position" - form valid - JD panel shown but position NOT yet in DB
  const [jdPanelOpen, setJdPanelOpen] = useState(false);
  // selectedJdForAttach: which JD the user picked in the comparison view ('original' | 'regenerated' | null)
  const [selectedJdForAttach, setSelectedJdForAttach] = useState(null);
  // jdAttached: user clicked "Attach JD" to confirm the chosen JD
  const [jdAttached, setJdAttached] = useState(false);
  const [attachingJd, setAttachingJd] = useState(false);
  const [showAttachSuccessModal, setShowAttachSuccessModal] = useState(false);
  const [attachedPositionForRanking, setAttachedPositionForRanking] = useState(null);

  // Comparison view states
  const [originalJd, setOriginalJd] = useState('');
  const [regeneratedJd, setRegeneratedJd] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('side-by-side'); // 'side-by-side' or 'single'
  const [showDiff, setShowDiff] = useState(false);

  // When navigating back from `/jd-preview/:positionId`, preserve the position fields
  // and (optionally) JD content so the "Back to Create Position" flow matches the in-page behavior.
  useEffect(() => {
    const st = location.state;
    if (!st?.fromJdPreview) return;

    if (st.positionId !== undefined && st.positionId !== null) {
      setPositionId(st.positionId);
    }

    if (typeof st.positionName === 'string') {
      setPositionName(st.positionName);
    }
    if (typeof st.clientName === 'string') {
      setClientName(st.clientName);
    }
    if (typeof st.jobCode === 'string') {
      setJobCode(st.jobCode);
    }

    if (typeof st.jdType === 'string' && st.jdType) {
      setJdType(st.jdType);
    }

    if (st.selectedCeipalJob) {
      setSelectedCeipalJob(st.selectedCeipalJob);
    }
    if (st.structuredData) {
      setStructuredData((prev) => ({ ...prev, ...st.structuredData }));
    }

    if (typeof st.jdContent === 'string' && st.jdContent.trim()) {
      if (st.jdType === 'ai' || st.jdType === 'ceipal') {
        setGeneratedJd(st.jdContent);
      } else if (st.jdType === 'upload') {
        setUploadedJd(st.jdContent);
      }
    }

    // When returning from JD Preview, keep the user on the JD Preview panel (Step 2),
    // so imported/generated/uploaded details are not "lost".
    setShowJdPreview(true);

    // If no real positionId exists yet (new position flow), re-open the JD panel so
    // the right column doesn't fall back to showing ExistingPositions.
    if (!st.positionId || st.positionId === 'null') {
      setJdPanelOpen(true);
    }

    if (st.jdType === 'ai') setActiveTab('structured');
    else if (st.jdType === 'upload') setActiveTab('upload');
    else if (st.jdType === 'ceipal') setActiveTab('ceipal');

    // Restore creation source best-effort. If we came back from a Ceipal-imported JD, treat as ceipal.
    // Otherwise, if we have a positionId at all, default to manual so Ceipal import stays blocked.
    if (st.jdType === 'ceipal') setPositionCreationSource('ceipal');
    else if (st.positionId !== undefined && st.positionId !== null) setPositionCreationSource('manual');

    // Restore comparison view so the user can re-select original vs regenerated.
    if (st.showComparison && st.originalJd && st.regeneratedJd) {
      setShowComparison(true);
      setOriginalJd(st.originalJd);
      setRegeneratedJd(st.regeneratedJd);
      if (st.selectedJdForAttach) setSelectedJdForAttach(st.selectedJdForAttach);
    }
  }, [location.state]);

  useEffect(() => {
    if (!positionId) return;
    const stored = getPositionJobFamilyKey(positionId);
    if (stored) setSelectedJobFamilyKey(stored);
  }, [positionId]);

  useEffect(() => {
    if (jobFamilyTouchedRef.current || positionId) return;
    const preview = previewJobFamilyForTitle(positionName.trim());
    if (!preview) {
      setSelectedJobFamilyKey('');
      setJobFamilyPreview(null);
      return;
    }
    if (preview.roleKey) {
      setSelectedJobFamilyKey(preview.roleKey);
      setJobFamilyPreview(null);
    } else {
      setSelectedJobFamilyKey('');
      setJobFamilyPreview(preview);
    }
  }, [positionName, positionId]);

  // Handle _openWizard signal from topbar "+ New Position" button
  useEffect(() => {
    if (location.state?._openWizard) {
      setView('wizard');
    }
  }, [location.state]);

  // JD Analytics funnel → Craft JD list (keep list view so PositionsTable can focus rows)
  useEffect(() => {
    if (location.state?.fromJdAnalytics) {
      setView('list');
    }
  }, [location.state]);

  // Enter wizard for a specific position (edit its JD) or from scratch (null)
  const enterWizardForPosition = (position) => {
    if (position) {
      setPositionName(position.positionName || '');
      setClientName(position.clientName || '');
      setJobCode(position.jobCode || '');
      setPositionId(position.positionId);
      setJdPanelOpen(true);
    } else {
      setPositionName('');
      setClientName('');
      setJobCode('');
      setPositionId(null);
      setSelectedJobFamilyKey('');
      setJobFamilyPreview(null);
      jobFamilyTouchedRef.current = false;
      setJdPanelOpen(false);
      setGeneratedJd('');
      setUploadedJd('');
      setJdType('');
      setShowJdPreview(false);
      setSelectedJdForAttach(null);
      setJdAttached(false);
    }
    setView('wizard');
  };

  // If a position was created manually, ensure the Ceipal tab cannot be active.
  useEffect(() => {
    if (positionCreationSource === 'manual' && activeTab === 'ceipal') {
      setActiveTab('structured');
    }
  }, [positionCreationSource, activeTab]);

  // Safety: if we have a positionId but no explicit creation source yet,
  // treat it as manual to prevent Ceipal import UI from showing.
  useEffect(() => {
    if (positionId && !positionCreationSource) {
      setPositionCreationSource('manual');
    }
  }, [positionId, positionCreationSource]);

  const extractJdTextFromResponse = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;

    // Backend DTO: { generatedJd, success, message }
    if (typeof data.generatedJd === 'string') return data.generatedJd;

    // Python JD service DTO: { job_description, word_count, character_count, generated_at }
    if (typeof data.job_description === 'string') return data.job_description;

    // Common alternates / nesting
    if (data.jobDescription && typeof data.jobDescription.jdText === 'string') return data.jobDescription.jdText;
    if (typeof data.jdText === 'string') return data.jdText;
    if (typeof data.content === 'string') return data.content;

    // Last resort: pull first string-ish known key
    for (const k of ['text', 'description', 'jd', 'jobDescription']) {
      const v = data?.[k];
      if (typeof v === 'string') return v;
    }

    return '';
  };

  const escapeHtml = (value) => {
    const s = String(value ?? '');
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Simple diff highlighting function
  const highlightDifferences = (original, regenerated) => {
    if (!original || !regenerated) return { original, regenerated };

    const originalLines = String(original).split('\n');
    const regeneratedLines = String(regenerated).split('\n');

    const maxLines = Math.max(originalLines.length, regeneratedLines.length);
    let highlightedOriginal = '';
    let highlightedRegenerated = '';

    for (let i = 0; i < maxLines; i++) {
      const origLine = escapeHtml(originalLines[i] || '');
      const regenLine = escapeHtml(regeneratedLines[i] || '');

      if (origLine !== regenLine) {
        highlightedOriginal += `<div style="background-color: #ffebee; border-left: 3px solid #f44336; padding: 4px 8px; margin: 2px 0;">${origLine}</div>`;
        highlightedRegenerated += `<div style="background-color: #e8f5e8; border-left: 3px solid #4caf50; padding: 4px 8px; margin: 2px 0;">${regenLine}</div>`;
      } else {
        highlightedOriginal += `<div style="padding: 4px 8px; margin: 2px 0;">${origLine}</div>`;
        highlightedRegenerated += `<div style="padding: 4px 8px; margin: 2px 0;">${regenLine}</div>`;
      }
    }

    return { original: highlightedOriginal, regenerated: highlightedRegenerated };
  };

  // Plain-text formatter for editors (no HTML tags/entities)
  const toPlainText = (value) => {
    if (value == null) return '';
    let s = String(value);
    // Convert explicit <br> tags to newlines first
    s = s.replace(/<br\s*\/?>/gi, '\n');
    // Strip all remaining tags
    s = s.replace(/<[^>]*>/g, '');
    // Decode common entities
    s = s
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\u00a0/g, ' ');
    // Normalize newlines
    s = s.replace(/\r\n|\r/g, '\n');
    // Collapse excessive blank lines
    s = s.replace(/\n{3,}/g, '\n\n');
    s = normalizeJdExperienceZeroToEntryLevel(s);
    return s.trim();
  };

  const [singleInput, setSingleInput] = useState('');
  const [structuredData, setStructuredData] = useState({
    job_role: '',
    experience: '',
    location: '',
    quick_input: '',
    position_type: 'Permanent Position',
    company_name: '',
    about_company: '',
  });
  const [structuredTouched, setStructuredTouched] = useState(false);
  const [structuredErrors, setStructuredErrors] = useState({});
  const [quickInputTouched, setQuickInputTouched] = useState(false);
  const [createTouched, setCreateTouched] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [jobTitleLimitModalOpen, setJobTitleLimitModalOpen] = useState(false);
  const jobTitleLimitNotifyTs = useRef(0);
  /** When validation flips to JOB_TITLE_EXCEED_MSG (length or long sentence), fire toast/modal once until fixed. */
  const prevJobTitleExceededRef = useRef(false);

  /** Only show modal for this validation scenario (no toast). */
  const notifyJobTitleLengthExceeded = useCallback(() => {
    const now = Date.now();
    if (now - jobTitleLimitNotifyTs.current < 120) return;
    jobTitleLimitNotifyTs.current = now;
    setJobTitleLimitModalOpen(true);
  }, []);

  /** Hard clamp: any code path (Ceipal import, bad state) cannot leave job_role longer than 100. */
  useLayoutEffect(() => {
    const v = structuredData.job_role;
    if (v.length <= JOB_TITLE_MAX_CHARS) return;
    notifyJobTitleLengthExceeded();
    const c = clampJobTitleRole(v);
    setStructuredData((prev) => ({ ...prev, job_role: c }));
  }, [structuredData.job_role, notifyJobTitleLengthExceeded]);

  const hasMeaningfulText = (value) => {
    // Treat common "invisible" characters (e.g., zero-width spaces) as whitespace
    // and require at least one letter/number so inputs like "////" or "????" don't pass.
    const s = String(value ?? '').replace(/[\s\u200B-\u200D\uFEFF]/g, '');
    return /[A-Za-z0-9]/.test(s);
  };

  const hasLetters = (value) => /[A-Za-z]/.test(String(value ?? ''));

  const isSymbolHeavy = (value) => {
    const collapsed = String(value ?? '').replace(/\s+/g, ' ').trim();
    const nonSpace = collapsed.replace(/\s/g, '');
    if (nonSpace.length < 8) return false;
    const alnumCount = (collapsed.match(/[A-Za-z0-9]/g) || []).length;
    return nonSpace.length > 0 && alnumCount / nonSpace.length < 0.5;
  };

  /** Job Title/Role: required, hard max 100 chars, and no long single-line "sentence" titles under 100. */
  const validateJobTitle = (value) => {
    const title = String(value ?? '').trim();
    if (title.length === 0) {
      return JOB_TITLE_REQUIRED_MSG;
    }
    if (title.length > JOB_TITLE_MAX_CHARS) {
      return JOB_TITLE_EXCEED_MSG;
    }
    if (isSentenceStyleRoleLine(title)) {
      return JOB_TITLE_EXCEED_MSG;
    }
    // Must contain letters and only allow limited punctuation.
    if (!hasLetters(title)) return GENERIC_INVALID_DETAILS_MSG;
    if (!/^[A-Za-z0-9][A-Za-z0-9\s&/().,\u2012\u2013\u2014\u2015\u2212+-]*$/.test(title)) return GENERIC_INVALID_DETAILS_MSG;
    if (isSymbolHeavy(title)) return GENERIC_INVALID_DETAILS_MSG;
    return undefined;
  };

  /** Free-form Quick Input: length cap; short single-line blobs use same sanity rules as Job Title. */
  const validateQuickInput = (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return undefined;
    if (!hasMeaningfulText(trimmed)) {
      return 'Quick input must contain at least one letter or number.';
    }
    if (trimmed.length > QUICK_INPUT_MAX_CHARS) {
      return `Quick input must be ${QUICK_INPUT_MAX_CHARS.toLocaleString()} characters or less.`;
    }
    const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const isShortSingleLine = lines.length <= 1 && trimmed.length <= 150;
    if (isShortSingleLine) {
      const jt = validateJobTitle(trimmed);
      if (jt) {
        if (jt === JOB_TITLE_REQUIRED_MSG) {
          return 'Quick input must contain at least one letter or number.';
        }
        return jt;
      }
    }
    return undefined;
  };

  /** Position/Client labels: simple behavior (required + max length only). */
  const validateReadableLabel = (value, maxLen, label) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return `${label} is required.`;
    if (trimmed.length > maxLen) return `${label} must be ${maxLen} characters or less.`;
    return undefined;
  };

  // Inline validation for Position Name: allow letters, digits, and ranges (e.g. "0-10"); reject symbol junk / repeats.
  const validatePositionNameField = (value) => {
    const base = validateReadableLabel(value, POSITION_NAME_MAX_CHARS, 'Position Name');
    if (base) return base;
    const t = String(value ?? '').trim();
    if (!hasMeaningfulText(t)) return GENERIC_INVALID_DETAILS_MSG;
    if (!/^[A-Za-z0-9][A-Za-z0-9\s&'().,\u2012\u2013\u2014\u2015\u2212+-]*$/.test(t)) return GENERIC_INVALID_DETAILS_MSG;
    if (isSymbolHeavy(t)) return GENERIC_INVALID_DETAILS_MSG;
    if (/(.)\1{6,}/.test(t)) return GENERIC_INVALID_DETAILS_MSG;
    return undefined;
  };

  const validateClientNameField = (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return 'Client Name is required.';
    if (trimmed.length > CLIENT_NAME_MAX_CHARS) return `Client Name must be ${CLIENT_NAME_MAX_CHARS} characters or less.`;
    if (!hasLetters(trimmed)) return GENERIC_INVALID_DETAILS_MSG;
    if (!/^[A-Za-z0-9][A-Za-z0-9\s&'().,\u2012\u2013\u2014\u2015\u2212+-]*$/.test(trimmed)) return GENERIC_INVALID_DETAILS_MSG;
    if (isSymbolHeavy(trimmed)) return GENERIC_INVALID_DETAILS_MSG;
    if (/(.)\1{6,}/.test(trimmed)) return GENERIC_INVALID_DETAILS_MSG;
    return undefined;
  };

  /** Job code: required; disallow negative/leading hyphen. */
  const validateJobCodeField = (value) => {
    const t = String(value ?? '').trim();
    if (!t) return 'Job Code is required.';
    if (t.startsWith('-')) return 'Job Code cannot start with "-".';
    if (t.length > JOB_CODE_MAX_CHARS) {
      return `Job Code must be ${JOB_CODE_MAX_CHARS} characters or less.`;
    }
    // Allow letters/digits in runs separated by spaces, hyphens, underscores, or slashes
    // (matches Ceipal-style codes like "JPC - 931"). Optional trailing separators avoid
    // flashing errors while the user is still typing (e.g. "JPC-").
    if (!/^[A-Za-z0-9]+(?:[\s\-_/]+[A-Za-z0-9]+)*[\s\-_/]*$/.test(t)) {
      return 'Job Code may only use letters, numbers, spaces, hyphens, underscores, and slashes.';
    }
    return undefined;
  };

  const renderFieldStatusIcon = ({ isInvalid, isValid, size = 14 }) => {
    if (isInvalid) {
      return <AlertCircle className="text-danger" size={size} />;
    }
    if (isValid) {
      return <CheckCircle2 className="text-success" size={size} />;
    }
    return null;
  };

  const validateStructuredInputs = (data) => {
    const next = {};
    const jt = validateJobTitle(data?.job_role);
    if (jt) next.job_role = jt;
    const expErr = validateExperienceField(data?.experience);
    if (expErr) next.experience = expErr;
    const locErr = validateWorkLocationField(data?.location);
    if (locErr) next.location = locErr;
    return next;
  };

  // Do not let Quick Input bypass invalid Job Title/Role when the user has typed in that field.
  const canGenerateFromQuickInput =
    hasMeaningfulText(singleInput) &&
    validateQuickInput(singleInput) === undefined &&
    !hasMeaningfulText(structuredData.job_role);
  const canGenerateFromStructured =
    !validateJobTitle(structuredData.job_role) &&
    isValidLocationField(structuredData.location) &&
    isValidExperienceField(structuredData.experience);
  const canGenerateJd = canGenerateFromQuickInput || canGenerateFromStructured;

  const handleGenerateClick = () => {
    // If button is disabled, don't do anything.
    if (jdGenerationLoading) return;
    if (!aiJdOn) { toast.info(AI_JD_OFF_MESSAGE); return; }
    if (!canGenerateJd) {
      setStructuredTouched(true);
      setStructuredErrors(validateStructuredInputs(structuredData));
      if (String(singleInput || '').trim()) {
        setQuickInputTouched(true);
      }
      return;
    }
    handleGenerateJd();
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [jdGenerationLoading, setJdGenerationLoading] = useState(false);
  const [jdImportLoading, setJdImportLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Ceipal import states
  // const [ceipalJobs, setCeipalJobs] = useState([]);
  // const [ceipalLoading, setCeipalLoading] = useState(false);
  const [showCeipalModal, setShowCeipalModal] = useState(false);
  const [selectedCeipalJob, setSelectedCeipalJob] = useState(null);
  const [ceipalSearchTerm, setCeipalSearchTerm] = useState('');
  const [usedJobCodes, setUsedJobCodes] = useState(new Set());
  const [usedJobCodesLoading, setUsedJobCodesLoading] = useState(false);
  const [ceipalJobFilter, setCeipalJobFilter] = useState('all'); // 'all' | 'new' | 'used'

  // Ceipal pagination states
  // const [ceipalCurrentPage, setCeipalCurrentPage] = useState(1);
  // const [ceipalRecordsPerPage, setCeipalRecordsPerPage] = useState(5);
  const [jobCode, setJobCode] = useState('');
  const [ceipalJobs, setCeipalJobs] = useState([]);
  const [ceipalCurrentPage, setCeipalCurrentPage] = useState(1);
  const [ceipalTotalPages, setCeipalTotalPages] = useState(1);
  const [ceipalRecordsPerPage, setCeipalRecordsPerPage] = useState(20);
  const [ceipalTotalJobs, setCeipalTotalJobs] = useState(0);
  const [ceipalAllJobs, setCeipalAllJobs] = useState([]);
  const [ceipalFullSearchLoading, setCeipalFullSearchLoading] = useState(false);
  const [ceipalLoading, setCeipalLoading] = useState(false);
  const [ceipalError, setCeipalError] = useState(null);
  // Cache and abort controller to reduce perceived latency when paginating
  const ceipalPageCacheRef = useRef({});
  const ceipalAbortRef = useRef(null);
  const ceipalAllJobsLoadingRef = useRef(false);

  const getWizardJdText = useCallback(() => {
    if (showComparison && selectedJdForAttach === 'original') return originalJd;
    if (showComparison && selectedJdForAttach === 'regenerated') return regeneratedJd;
    if (jdType === 'upload') return uploadedJd;
    return generatedJd;
  }, [showComparison, selectedJdForAttach, originalJd, regeneratedJd, jdType, uploadedJd, generatedJd]);

  const getWizardExperience = useCallback(
    () => String(structuredData.experience || selectedCeipalJob?.experience || '').trim(),
    [structuredData.experience, selectedCeipalJob]
  );

  const syncJobFamilyFromContext = useCallback(async (title, jdText, experience) => {
    if (jobFamilyTouchedRef.current || !String(title || '').trim()) return null;
    try {
      const result = await autoFetchJobFamilyForPosition({
        title: String(title).trim(),
        jdText,
        experience,
        generateSkills: true,
      });
      if (result.roleKey && isValidRoleKey(result.roleKey)) {
        setSelectedJobFamilyKey(result.roleKey);
        setJobFamilyPreview(null);
        rememberLastCreatedRoleKey(result.roleKey);
        if (positionId) setPositionJobFamilyKey(positionId, result.roleKey);
        return result.roleKey;
      }
      return null;
    } catch (error) {
      console.warn('Job family sync failed:', error);
      return null;
    }
  }, [positionId]);

  useEffect(() => {
    const title = positionName.trim();
    const jdText = getWizardJdText();
    if (!title || !jdText?.trim() || jobFamilyTouchedRef.current) return;
    syncJobFamilyFromContext(title, jdText, getWizardExperience());
  }, [positionName, generatedJd, uploadedJd, jdType, getWizardJdText, getWizardExperience, syncJobFamilyFromContext]);

  // Debug useEffect to track state changes
  useEffect(() => {
    console.log('State check:', {
      showJdPreview,
      generatedJd: generatedJd ? 'HAS CONTENT' : 'EMPTY',
      uploadedJd: uploadedJd ? 'HAS CONTENT' : 'EMPTY',
      jdType,
      positionId
    });

    // Check if Review & Finalize should be visible
    if (generatedJd || uploadedJd) {
      console.log('✅ Review & Finalize should be visible now!');
    } else {
      console.log('❌ Review & Finalize not visible - no JD content');
    }
  }, [showJdPreview, generatedJd, uploadedJd, jdType, positionId]);

  // Search effect for CEIPAL jobs.
  // Filtering is client-side (instant once ceipalAllJobs is loaded).
  // We only debounce the server call when the user clears the search (to restore paginated view).
  useEffect(() => {
    if (!showCeipalModal) return;
    if (ceipalSearchTerm.trim()) {
      // Kick off background preload if not yet started — filter result updates instantly via memo.
      fetchAllCeipalJobs();
      return;
    }
    // User cleared the search — restore server-paginated view after brief delay.
    const timer = setTimeout(() => {
      handleImportFromCeipal(1, '');
    }, 200);
    return () => clearTimeout(timer);
  }, [ceipalSearchTerm, showCeipalModal]);

  const fetchAllCeipalJobs = async () => {
    // Skip if already loaded or currently loading.
    if (ceipalAllJobs.length > 0 || ceipalAllJobsLoadingRef.current) return;

    ceipalAllJobsLoadingRef.current = true;
    setCeipalFullSearchLoading(true);

    try {
      const pageSize = 200;
      const firstResponse = await api.get(`/ceipal/job-postings?page=1&pageSize=${pageSize}&search=`);
      if (!firstResponse.data?.success) return;

      const firstPageJobs = firstResponse.data.data || [];
      const totalPages = firstResponse.data.totalPages || 1;

      let allJobs = [...firstPageJobs];

      // Fetch all remaining pages in parallel for maximum speed.
      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const pageResults = await Promise.allSettled(
          remainingPages.map((page) =>
            api.get(`/ceipal/job-postings?page=${page}&pageSize=${pageSize}&search=`)
          )
        );
        for (const result of pageResults) {
          if (result.status === 'fulfilled' && result.value.data?.success) {
            allJobs = allJobs.concat(result.value.data.data || []);
          }
        }
      }

      // De-duplicate by stable job identifier.
      const dedupedJobs = Array.from(
        new Map(
          allJobs.map((job) => [String(job.job_id || job.id || job.job_code), job])
        ).values()
      );

      setCeipalAllJobs(dedupedJobs);
    } catch (error) {
      console.error('Error preloading full Ceipal dataset for search:', error);
    } finally {
      ceipalAllJobsLoadingRef.current = false;
      setCeipalFullSearchLoading(false);
    }
  };

  const handleCreatePosition = () => {
    // Prevent re-entry if position already created or JD panel already open
    if (loading || positionId || jdPanelOpen) return;
    const positionNameErr = validatePositionNameField(positionName);
    const clientNameErr = validateClientNameField(clientName);
    const jobCodeErr = validateJobCodeField(jobCode);
    const hasValidationErrors = [positionNameErr, clientNameErr, jobCodeErr].some(Boolean);
    if (hasValidationErrors) {
      setCreateTouched(true);
      setCreateErrors({
        positionName: positionNameErr || undefined,
        clientName: clientNameErr || undefined,
        jobCode: jobCodeErr || undefined,
      });
      return;
    }
    setCreateTouched(false);
    setCreateErrors({});
    // Open the JD panel without hitting DB — position is created only when generation/upload starts
    setJdPanelOpen(true);
    setPositionCreationSource(selectedCeipalJob ? 'ceipal' : 'manual');

    // If a Ceipal job is already selected, auto-import its JD so the user
    // lands directly on the JD preview instead of the tab selection screen.
    if (selectedCeipalJob) {
      handleImportCeipalJd(selectedCeipalJob);
    }
  };

  // Creates the position in DB if not already done. Called just before JD generation/upload.
  const ensurePositionCreated = useCallback(async () => {
    if (positionId && positionId !== 'null') return positionId;
    const jobCodeTrimmed = String(jobCode ?? '').trim();
    setLoading(true);
    try {
      if (jobCodeTrimmed) {
        const existsResp = await api.get('/positions/exists', { params: { jobCode: jobCodeTrimmed } });
        if (existsResp?.data === true) {
          toast.error('Job Code already exists. Please enter a unique Job Code.');
          return null;
        }
      }
      const positionData = {
        positionName: positionName.trim(),
        clientName: clientName.trim(),
        jobCode: jobCodeTrimmed,
      };
      const response = await api.post('/positions', positionData);
      const newPositionId = response.data.positionId;
      setPositionId(newPositionId);
      setPositionCreationSource(selectedCeipalJob ? 'ceipal' : 'manual');
      const jfResult = await autoFetchJobFamilyForPosition({
        title: positionName.trim(),
        jdText: getWizardJdText(),
        experience: getWizardExperience(),
        generateSkills: true,
      });
      const roleKey = jfResult?.roleKey && isValidRoleKey(jfResult.roleKey) ? jfResult.roleKey : null;
      if (roleKey) {
        setPositionJobFamilyKey(newPositionId, roleKey);
        setSelectedJobFamilyKey(roleKey);
        setJobFamilyPreview(null);
        rememberLastCreatedRoleKey(roleKey);
      }
      return newPositionId;
    } catch (error) {
      const status = error?.response?.status;
      const serverCode = error?.response?.data?.code;
      const serverMsg = error?.response?.data?.message;
      const entered = jobCodeTrimmed;
      if (
        status === 409 ||
        serverCode === 'DUPLICATE_JOB_CODE' ||
        (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('job code') && serverMsg.toLowerCase().includes('already')) ||
        (entered && !error?.response)
      ) {
        toast.error('Job Code already exists. Please enter a unique Job Code.');
        return null;
      }
      if (entered) {
        try {
          const existsResp = await api.get('/positions/exists', { params: { jobCode: entered } });
          if (existsResp?.data === true) {
            toast.error('Job Code already exists. Please enter a unique Job Code.');
            return null;
          }
        } catch (_) { /* ignore */ }
      }
      toast.error(resolveApiErrorMessage(error, 'Failed to create position. Please try again.'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [positionId, jobCode, positionName, clientName, selectedCeipalJob, selectedJobFamilyKey, getWizardJdText, getWizardExperience]);

  const handleGenerateJd = async () => {
    if (!aiJdOn) { toast.info(AI_JD_OFF_MESSAGE); return; }
    setJdGenerationLoading(true);

    try {
      // No DB hit here — position is created only when the user clicks "Attach JD".
      setShowComparison(false);
      setSelectedJdForAttach(null);
      setJdAttached(false);

      // Prepare request data based on input method
      let requestData;

      // If regenerating from Ceipal, send the existing JD content (best signal).
      if (jdType === 'ceipal' && selectedCeipalJob) {
        // IMPORTANT: backend expects { jobInput }, not { singleInput }.
        // Also: when regenerating, pass the original JD (if available) as the source of truth.
        const original = (generatedJd && typeof generatedJd === 'string') ? generatedJd : '';
        const singleInputText = original.trim()
          ? `Regenerate this Job Description, preserving all role-specific skills, responsibilities, tools, and seniority. Do not add unrelated technologies.\n\nORIGINAL JD:\n${original}`
          : `Generate a job description for ${selectedCeipalJob.position_title}`;
        requestData = {
          jobInput: singleInputText
        };

        const response = await api.post(`/job-descriptions/preview/single`, requestData);
        console.log('AI JD generated successfully:', response.data);
        console.log('Full response structure:', JSON.stringify(response.data, null, 2));

        if (response?.data && response.data.success === false) {
          const m = toastMessageForJdApiFailure(response.data.message, JD_GENERATE_FAIL_SHORT);
          if (m === JOB_TITLE_EXCEED_MSG) notifyJobTitleLengthExceeded();
          else toast.error(m);
          return;
        }

        let jdContent = extractJdTextFromResponse(response.data);

        console.log('JD Content found:', jdContent);
        setGeneratedJd(jdContent);
        setRegeneratedJd(jdContent);
        setJdType('ai'); // Change type to AI after regeneration
      } else if (canGenerateFromStructured) {
        // Prefer validated structured fields whenever they are complete — do not let Quick Input override.
        const nextErrors = validateStructuredInputs(structuredData);
        setStructuredTouched(true);
        setStructuredErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
          if (nextErrors.job_role === JOB_TITLE_EXCEED_MSG) {
            notifyJobTitleLengthExceeded();
          }
          return;
        }

        requestData = {
          jobTitle: clampJobTitleRole(structuredData.job_role).trim(),
          experienceInYears: String(structuredData.experience).trim(),
          preferredLocation: structuredData.location,
          quickInput: singleInput || '',
          positionType: structuredData.position_type || 'Permanent Position',
          companyName: structuredData.company_name || '',
          aboutCompany: structuredData.about_company || '',
        };

        const response = await api.post(`/job-descriptions/preview/structured`, requestData);
        console.log('AI JD generated successfully:', response.data);
        console.log('Full response structure:', JSON.stringify(response.data, null, 2));

        if (response?.data && response.data.success === false) {
          const m = toastMessageForJdApiFailure(response.data.message, JD_GENERATE_FAIL_SHORT);
          if (m === JOB_TITLE_EXCEED_MSG) notifyJobTitleLengthExceeded();
          else toast.error(m);
          return;
        }

        let jdContent = extractJdTextFromResponse(response.data);

        if (typeof jdContent === 'string') {
          jdContent = normalizeJdExperienceZeroToEntryLevel(jdContent);
        }

        console.log('JD Content found:', jdContent);
        setGeneratedJd(jdContent);
      } else if (hasMeaningfulText(structuredData.job_role)) {
        // Never fall through to Quick Input while Job Title/Role has content but is incomplete or invalid.
        const nextErrors = validateStructuredInputs(structuredData);
        setStructuredTouched(true);
        setStructuredErrors(nextErrors);
        if (nextErrors.job_role === JOB_TITLE_EXCEED_MSG) {
          notifyJobTitleLengthExceeded();
        }
        return;
      } else if (singleInput.trim()) {
        const quickErr = validateQuickInput(singleInput);
        if (quickErr) {
          toast.error(quickErr);
          setQuickInputTouched(true);
          return;
        }

        requestData = {
          jobInput: singleInput.trim()
        };

        const response = await api.post(`/job-descriptions/preview/single`, requestData);
        console.log('AI JD generated successfully:', response.data);
        console.log('Full response structure:', JSON.stringify(response.data, null, 2));

        if (response?.data && response.data.success === false) {
          const m = toastMessageForJdApiFailure(response.data.message, JD_GENERATE_FAIL_SHORT);
          if (m === JOB_TITLE_EXCEED_MSG) notifyJobTitleLengthExceeded();
          else toast.error(m);
          return;
        }

        let jdContent = extractJdTextFromResponse(response.data);

        console.log('JD Content found:', jdContent);
        setGeneratedJd(jdContent);
      } else if (jdType === 'ai' && generatedJd && typeof generatedJd === 'string') {
        // Regenerate from the current AI JD only when structured / quick paths do not apply.
        const original = generatedJd;
        const singleInputText = original.trim()
          ? `Regenerate this Job Description, preserving all role-specific skills, responsibilities, tools, and seniority. Do not add unrelated technologies.\n\nORIGINAL JD:\n${original}`
          : `Generate a job description for ${positionName || 'this position'}`;
        requestData = {
          jobInput: singleInputText
        };

        const response = await api.post(`/job-descriptions/preview/single`, requestData);
        console.log('AI JD regenerated successfully:', response.data);
        console.log('Full response structure:', JSON.stringify(response.data, null, 2));

        if (response?.data && response.data.success === false) {
          toast.error(response.data.message || 'Failed to regenerate job description.');
          return;
        }

        let jdContent = response.data.generatedJd || response.data.jdText || response.data.content || response.data;
        console.log('JD Content found:', jdContent);
        setGeneratedJd(jdContent);
        setRegeneratedJd(jdContent);
        setJdType('ai');
      } else if (jdType === 'upload') {
        // If regenerating from uploaded JD, use single input API with position name
        const original = (uploadedJd && typeof uploadedJd === 'string') ? uploadedJd : '';
        const singleInputText = original.trim()
          ? `Regenerate this Job Description, preserving all role-specific skills, responsibilities, tools, and seniority. Do not add unrelated technologies.\n\nORIGINAL JD:\n${original}`
          : `Generate a job description for ${positionName}`;
        requestData = {
          jobInput: singleInputText
        };

        const response = await api.post(`/job-descriptions/preview/single`, requestData);
        console.log('AI JD generated successfully:', response.data);
        console.log('Full response structure:', JSON.stringify(response.data, null, 2));

        if (response?.data && response.data.success === false) {
          toast.error(response.data.message || 'Failed to regenerate job description.');
          return;
        }

        let jdContent = extractJdTextFromResponse(response.data);

        console.log('JD Content found:', jdContent);
        setGeneratedJd(jdContent);
        setRegeneratedJd(jdContent);
        setJdType('ai'); // Change type to AI after regeneration
      } else {
        const nextErrors = validateStructuredInputs(structuredData);
        setStructuredTouched(true);
        setStructuredErrors(nextErrors);
        if (nextErrors.job_role === JOB_TITLE_EXCEED_MSG) {
          notifyJobTitleLengthExceeded();
        }
        return;
      }

      // Set states in the correct order
      setJdType('ai');
      setShowJdPreview(true);
      setForceUpdate(prev => prev + 1); // Force re-render

      // Notify other components that a new AI JD has been generated for this position
      try {
        window.dispatchEvent(new CustomEvent('jd:regenerated', { detail: { positionId } }));
        console.log('Dispatched jd:regenerated event for position:', positionId);
      } catch (e) {
        console.warn('Failed to dispatch jd:regenerated event', e);
      }

      console.log('AI generation completed, states set:', {
        showJdPreview: true,
        jdType: 'ai',
        generatedJd: 'content set'
      });

    } catch (error) {
      console.error('Error generating JD:', error);
      const m = toastMessageForGenerateJdCatch(error);
      if (m === JOB_TITLE_EXCEED_MSG) notifyJobTitleLengthExceeded();
      else toast.error(m);
    } finally {
      setJdGenerationLoading(false);
    }
  };

  // Regenerate using the currently displayed JD as ORIGINAL JD (works for AI / Upload / Ceipal)
  const handleRegenerateCurrentJd = async () => {
    if (!aiJdOn) { toast.info(AI_JD_OFF_MESSAGE); return; }
    setJdGenerationLoading(true);
    setSelectedJdForAttach(null);
    setJdAttached(false);
    try {
      const currentJdText =
        jdType === 'upload'
          ? (uploadedJd && typeof uploadedJd === 'string' ? uploadedJd : '')
          : (generatedJd && typeof generatedJd === 'string' ? generatedJd : '');

      if (!currentJdText.trim()) {
        toast.error('No JD content found to regenerate.');
        return;
      }

      const requestData = {
        jobInput: `Regenerate this Job Description, preserving all role-specific skills, responsibilities, tools, and seniority. Do not add unrelated technologies.\n\nORIGINAL JD:\n${currentJdText}`
      };

      const response = await api.post(`/job-descriptions/preview/single`, requestData);
      console.log('JD regenerated successfully:', response.data);

      if (response?.data && response.data.success === false) {
        toast.error(response.data.message || 'Failed to regenerate job description.');
        return;
      }

      let jdContent = response.data.generatedJd || response.data.jdText || response.data.content || response.data;
      // Ensure we always compare/render strings (some APIs can return objects)
      if (typeof jdContent !== 'string') {
        try {
          jdContent = JSON.stringify(jdContent, null, 2);
        } catch {
          jdContent = String(jdContent ?? '');
        }
      }
      const normalized = (v) => String(v ?? '').replace(/\r\n|\r/g, '\n').trim();
      const normalizedOriginal = normalized(currentJdText);
      const normalizedRegen = normalized(jdContent);

      if (!normalizedRegen) {
        toast.error('Regenerated JD is empty. Please try again.');
        return;
      }

      // Preserve original + show comparison for AI as well (do not overwrite original JD).
      // Upload + Ceipal already supported; extend the same behavior to AI.
      if (jdType === 'upload' || jdType === 'ceipal' || jdType === 'ai') {
        setOriginalJd(currentJdText);
        setRegeneratedJd(jdContent);
        setComparisonMode('side-by-side');
        setShowComparison(true);
        setShowJdPreview(true);
        if (normalizedOriginal === normalizedRegen) {
          toast.info('No changes detected between original and regenerated JD.');
        }
      } else {
        // Defensive fallback: treat unknown types as AI compare behavior
        setOriginalJd(currentJdText);
        setRegeneratedJd(jdContent);
        setComparisonMode('side-by-side');
        setShowComparison(true);
        setShowJdPreview(true);
      }
      toast.success('JD regenerated successfully');
    } catch (error) {
      console.error('Error regenerating JD:', error);
      toast.error(error?.response?.data?.message || 'Failed to regenerate job description. Please try again.');
    } finally {
      setJdGenerationLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const ALLOWED_UPLOAD_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt'];
  const getFileExtension = (fileName = '') => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  const validateUploadFile = (file) => {
    if (!file) {
      return { isValid: false, message: 'No file selected.' };
    }

    const extension = getFileExtension(file.name);
    const isValidExtension = ALLOWED_UPLOAD_EXTENSIONS.includes(extension);

    if (!isValidExtension) {
      return {
        isValid: false,
        message: 'Unsupported file format. Please upload PDF, DOC, DOCX, or TXT files only.'
      };
    }

    return { isValid: true, message: '' };
  };

  const setValidatedUploadFile = (file) => {
    const validation = validateUploadFile(file);
    if (!validation.isValid) {
      setSelectedFile(null);
      toast.error(validation.message);
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setValidatedUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setValidatedUploadFile(e.target.files[0]);
      e.target.value = '';
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) return;
    const validation = validateUploadFile(selectedFile);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }
    setUploadLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      console.log('Extracting text from JD file:', selectedFile.name);

      const response = await api.post('/job-descriptions/extract-text', formData);

      console.log('JD text extracted successfully:', response.data);

      if (!response.data?.success) {
        toast.error(response.data?.error || 'Failed to extract text from file.');
        return;
      }

      let jdContent = response.data.extractedText || '';
      console.log('Extracted JD Content found:', jdContent);

      if (!jdContent.trim()) {
        toast.error('Could not extract text from the uploaded file. Please try a different file.');
        return;
      }

      // Normalize common escaped newlines from APIs
      jdContent = jdContent.replace(/\\n/g, '\n');

      setUploadedJd(jdContent);
      setJdType('upload');
      setShowJdPreview(true);
      setSelectedFile(null);
      console.log('Upload completed, states set:', {
        showJdPreview: true,
        jdType: 'upload',
        uploadedJd: 'content set'
      });

    } catch (error) {
      console.error('Error uploading JD:', error);
      toast.error('Failed to upload job description. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCreateNewPosition = () => {
    setPositionName('');
    setClientName('');
    setGeneratedJd('');
    setUploadedJd('');
    setJdType('');
    setShowJdPreview(false);
    setPositionId(null);
    setLoading(false);
    setActiveTab('structured');
    setPositionCreationSource('');
    setSingleInput('');
    setStructuredData({
      job_role: '',
      experience: '',
      location: ''
    });
    setSelectedFile(null);
    setJobCode('');
    setSelectedCeipalJob(null);
    setJdImportLoading(false);
    // Reset new JD flow states
    setJdPanelOpen(false);
    setSelectedJdForAttach(null);
    setJdAttached(false);
    // Reset comparison states
    setOriginalJd('');
    setRegeneratedJd('');
    setShowComparison(false);
    setComparisonMode('side-by-side');
    setShowDiff(false);
    setSelectedJobFamilyKey('');
    setJobFamilyPreview(null);
    jobFamilyTouchedRef.current = false;
  };

  const handleAttachJd = async () => {
    setAttachingJd(true);
    try {
      // Ensure position exists in DB before attaching.
      // Treat string "null" (can come back from JdPreview useParams) same as missing.
      let activePositionId = (positionId && positionId !== 'null') ? positionId : null;
      if (!activePositionId) {
        activePositionId = await ensurePositionCreated();
        if (!activePositionId) return;
      }

      // Determine which JD text to attach
      let finalJdText;
      if (showComparison) {
        finalJdText = selectedJdForAttach === 'original' ? originalJd : regeneratedJd;
      } else {
        finalJdText = jdType === 'upload' ? uploadedJd : generatedJd;
      }

      if (!finalJdText || !finalJdText.trim()) {
        toast.error('No JD content to attach.');
        return;
      }

      // Map jdType to sourceType
      const sourceTypeMap = { ai: 'AI_GENERATED', upload: 'UPLOADED', ceipal: 'CEIPAL_IMPORT' };
      const sourceType = sourceTypeMap[jdType] || 'ATTACHED';

      const resp = await api.post(`/job-descriptions/position/${activePositionId}/attach`, {
        jdText: normalizeJdForStorage(finalJdText),
        sourceType
      });

      if (!resp.data?.success) {
        toast.error(resp.data?.error || 'Failed to attach JD.');
        return;
      }

      let roleKey = getPositionJobFamilyKey(activePositionId);
      if (!roleKey || !isValidRoleKey(roleKey)) {
        const jfResult = await autoFetchJobFamilyForPosition({
          title: positionName.trim(),
          jdText: finalJdText,
          experience: getWizardExperience(),
          generateSkills: true,
        });
        if (jfResult?.roleKey && isValidRoleKey(jfResult.roleKey)) {
          roleKey = jfResult.roleKey;
        } else if (jobFamilyPreview?.sectorId) {
          const seated = await ensureRoleSeatedInJobFamily({
            title: jobFamilyPreview.pendingRoleTitle || positionName.trim(),
            sectorId: jobFamilyPreview.sectorId,
            familyId: jobFamilyPreview.familyId || '',
            jdText: finalJdText,
            experience: getWizardExperience(),
            generateSkills: true,
          });
          if (seated.roleKey && isValidRoleKey(seated.roleKey)) roleKey = seated.roleKey;
        } else {
          roleKey = await resolveRoleKeyForPosition(positionName.trim(), {
            userSelectedKey: selectedJobFamilyKey,
            userTouched: jobFamilyTouchedRef.current,
            jdText: finalJdText,
            experience: getWizardExperience(),
          });
        }
      }
      if (roleKey && isValidRoleKey(roleKey)) {
        setPositionJobFamilyKey(activePositionId, roleKey);
        setSelectedJobFamilyKey(roleKey);
        setJobFamilyPreview(null);
        rememberLastCreatedRoleKey(roleKey);
      }
      const jdId = resp.data.jdId;
      if (jdId) {
        const { skills } = await ensurePositionSkillWeightages({
          positionId: activePositionId,
          positionTitle: positionName.trim(),
          jdText: finalJdText,
          experience: getWizardExperience(),
          roleKey: roleKey || '',
        });
        if (skills.length) {
          await syncJobFamilySkillsToJd(api, jdId, skills);
        }
      }

      // Update UI state to reflect the selected JD
      if (showComparison) {
        const chosenJd = selectedJdForAttach === 'original' ? originalJd : regeneratedJd;
        if (jdType === 'upload') setUploadedJd(chosenJd);
        else setGeneratedJd(chosenJd);
        setShowComparison(false);
        setOriginalJd('');
        setRegeneratedJd('');
      }

      setJdAttached(true);
      setSelectedJdForAttach(null);
      setAttachedPositionForRanking(activePositionId);
      setShowAttachSuccessModal(true);
    } catch (error) {
      toast.error('Failed to attach JD. Please try again.');
    } finally {
      setAttachingJd(false);
    }
  };

  // Ceipal import functions
  const handleImportFromCeipal = async (page = 1, search = '', pageSizeOverride = null) => {
    if (positionCreationSource === 'manual') {
      toast.error('Ceipal JD import is disabled for manually created positions.');
      return;
    }
    // Handle event objects from pagination component
    if (typeof page === 'object') page = 1;
    const safePage = Number(page) > 0 ? Number(page) : 1;
    const effectivePageSize = Number(pageSizeOverride) > 0 ? Number(pageSizeOverride) : ceipalRecordsPerPage;

    console.log('=== CEIPAL FETCH DEBUG ===');
    console.log('Requesting page:', safePage);
    console.log('Page size:', effectivePageSize);
    console.log('Search term:', search);
    console.log('API URL:', `/ceipal/job-postings?page=${safePage}&pageSize=${effectivePageSize}&search=${search}`);
    
    setCeipalError(null);

    // If we have cached data for this page + pageSize + search, show it immediately (no spinner jump)
    const cacheKey = `${safePage}|${effectivePageSize}|${search}`;
    const cached = ceipalPageCacheRef.current[cacheKey];
    if (cached) {
      setCeipalJobs(cached.jobs);
      setCeipalTotalPages(cached.totalPages);
      setCeipalTotalJobs(cached.totalJobs);
      setCeipalCurrentPage(cached.currentPage);
      // No loading state so UI stays responsive
    } else {
      setCeipalLoading(true);
    }
    
    try {
      // Cancel any in-flight request before starting a new one
      if (ceipalAbortRef.current) {
        ceipalAbortRef.current.abort();
      }
      ceipalAbortRef.current = new AbortController();
      const response = await api.get(`/ceipal/job-postings?page=${safePage}&pageSize=${effectivePageSize}&search=${encodeURIComponent(search)}`);
      console.log('=== BACKEND RESPONSE ===');
      console.log('Full response:', response.data);
      console.log('Jobs received:', response.data.data?.length || 0);
      console.log('Total count:', response.data.totalCount);
      console.log('Total pages:', response.data.totalPages);
      console.log('Current page:', response.data.currentPage || safePage);
      console.log('Page size:', response.data.pageSize);
  
      if (response.data.success) {
        // Set the jobs data from backend
        const jobs = response.data.data || [];
        const visibleJobs = jobs.slice(0, effectivePageSize);
        console.log('jobs',jobs)
        setCeipalJobs(visibleJobs);
        console.log('Jobs set in state:', jobs.length);
  
        // Set pagination info from backend
        const totalJobs = response.data.totalCount || jobs.length;
        const totalPages = Math.max(1, Math.ceil(totalJobs / effectivePageSize));
        const currentPage = response.data.currentPage || safePage;
        
        setCeipalTotalPages(totalPages);
        setCeipalTotalJobs(totalJobs);
        setCeipalCurrentPage(currentPage);

        // Update cache for this page + search combination
        ceipalPageCacheRef.current[cacheKey] = {
          jobs: visibleJobs,
          totalPages,
          totalJobs,
          currentPage,
          pageSize: effectivePageSize
        };
        
        console.log('Pagination set:', {
          currentPage: currentPage,
          totalPages: totalPages,
          totalJobs: totalJobs,
          pageSize: effectivePageSize,
          jobsOnThisPage: visibleJobs.length
        });
  
        // Open modal only on first load
        if (safePage === 1) {
          setShowCeipalModal(true);
        }

        // Prefetch next page in background (if exists) to improve perceived speed
        const nextPage = currentPage + 1;
        if (nextPage <= totalPages) {
          const nextKey = `${nextPage}|${effectivePageSize}|${search}`;
          if (!ceipalPageCacheRef.current[nextKey]) {
            // Fire and forget; do not touch UI state here
            api
              .get(`/ceipal/job-postings?page=${nextPage}&pageSize=${effectivePageSize}&search=${encodeURIComponent(search)}`)
              .then((nextResp) => {
                if (nextResp.data && nextResp.data.success) {
                  const prefetchedJobs = (nextResp.data.data || []).slice(0, effectivePageSize);
                  ceipalPageCacheRef.current[nextKey] = {
                    jobs: prefetchedJobs,
                    totalPages: nextResp.data.totalPages || totalPages,
                    totalJobs: nextResp.data.totalCount || totalJobs,
                    currentPage: nextResp.data.currentPage || nextPage,
                    pageSize: effectivePageSize
                  };
                }
              })
              .catch(() => { /* ignore prefetch errors */ });
          }
        }
      } else {
        const errorMsg = response.data.error || 'Failed to fetch jobs from Ceipal';
        console.error('Backend returned error:', errorMsg);
        setCeipalError(errorMsg);
      }
    } catch (error) {
      console.error('=== API ERROR ===');
      console.error('Error object:', error);
      console.error('Error response:', error.response?.data);
      if (error.name === 'CanceledError') {
        // Request was canceled; do not show error toast
        console.log('Ceipal fetch canceled');
      } else {
        setCeipalError('Failed to fetch jobs from Ceipal: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setCeipalLoading(false);
      console.log('=== FETCH COMPLETE ===\n');
    }
  };
  const handleSelectCeipalJob = async (job) => {
    const normalizedCode = (job.job_code || '').toLowerCase().trim();

    // Fast path: already in the in-memory set (loaded when modal opened).
    if (normalizedCode && usedJobCodes.has(normalizedCode)) {
      toast.error(`Job code "${job.job_code}" already has a position created. Select an available job.`);
      return;
    }

    // Fallback: verify against DB directly in case the used-job-codes fetch was still in-flight.
    if (normalizedCode) {
      try {
        const resp = await api.get('/positions/exists', { params: { jobCode: normalizedCode } });
        if (resp?.data === true) {
          // Update the local set so the row becomes visually disabled too.
          setUsedJobCodes(prev => new Set([...prev, normalizedCode]));
          toast.error(`Job code "${job.job_code}" already has a position created. Select an available job.`);
          return;
        }
      } catch (_) { /* non-critical: allow selection if check fails */ }
    }

    setSelectedCeipalJob(job);
    const title = normalizeImportedAtsLabel(job.position_title);
    setPositionName(title);
    setJobCode(job.job_code);
    setCreateErrors((prev) => ({ ...prev, positionName: undefined, jobCode: undefined }));
    setStructuredData({
      job_role: clampJobTitleRole(title),
      experience: job.experience || '',
      location: normalizeImportedAtsLabel(job.city || job.country || '')
    });
    setShowCeipalModal(false);
  };

  const handleImportCeipalJd = async (job, positionIdOverride = null) => {
    try {
      if (positionCreationSource === 'manual') {
        toast.error('Ceipal JD import is disabled for manually created positions.');
        return;
      }
      // Set loading state for import
      setJdImportLoading(true);

      // Match Upload JD behavior: importing a new Ceipal JD should NOT auto-show comparison.
      // Clear any previous regeneration comparison state.
      setOriginalJd('');
      setRegeneratedJd('');
      setShowComparison(false);
      setComparisonMode('side-by-side');
      setShowDiff(false);

      // Set position details from Ceipal job (Unicode dashes → ASCII so validators match ATS text)
      // Only overwrite positionName if the user has NOT manually edited it away from the original Ceipal title.
      const importedTitle = normalizeImportedAtsLabel(job.position_title);
      const originalTitle = normalizeImportedAtsLabel(job.position_title);
      setPositionName(prev => {
        const trimmedPrev = (prev || '').trim();
        const trimmedOriginal = (originalTitle || '').trim();
        // If user changed the name from the Ceipal default, keep their edit
        return (trimmedPrev && trimmedPrev !== trimmedOriginal) ? prev : importedTitle;
      });
      setJobCode(job.job_code);
      setSelectedCeipalJob(job);

      let jdContent = null;

      // Check if job description is already available in the job data
      if (job.job_description || job.description) {
        // Use the job description from the job data
        jdContent = job.job_description || job.description;
      } else {
        // Try to get job description using the new endpoint
        try {
          const response = await api.get(`/ceipal/job-description/${job.job_id || job.id}`);
          if (response.data && response.data.success && response.data.job_description) {
            jdContent = response.data.job_description;
          } else {
            toast.error('No job description found for this position in Ceipal.');
            return;
          }
        } catch (apiError) {
          console.error('Error fetching job description:', apiError);
          toast.error('No job description found for this position in Ceipal.');
          return;
        }
      }

      if (typeof jdContent === 'string') {
        jdContent = normalizeJdHtmlFromSource(jdContent);
      }

      setGeneratedJd(jdContent);
      setJdType('ceipal');
      setShowJdPreview(true);

      const resolvedTitle = (() => {
        const trimmedPrev = (positionName || '').trim();
        const trimmedOriginal = (originalTitle || '').trim();
        return (trimmedPrev && trimmedPrev !== trimmedOriginal) ? trimmedPrev : importedTitle;
      })();
      await syncJobFamilyFromContext(
        resolvedTitle,
        jdContent,
        String(job.experience || structuredData.experience || '').trim()
      );

      toast.success('JD imported from Ceipal. Click "Attach JD" to save it to this position.');
    } catch (error) {
      console.error('Error importing JD from Ceipal:', error);
      toast.error('Failed to import JD from Ceipal. Please try again.');
    } finally {
      setJdImportLoading(false);
    }
  };

  const isCeipalSearchActive = ceipalSearchTerm.trim().length > 0;

  // Search should filter against the full dataset using partial contains across
  // job code, position title, and location.
  const filteredCeipalJobs = useMemo(() => {
    const normalizedSearch = ceipalSearchTerm.trim().toLowerCase();
    const sourceJobs = normalizedSearch
      ? ceipalAllJobs // For search, always use full preloaded dataset.
      : ceipalJobs;   // For default view, keep server-paginated data.
    if (!normalizedSearch) return sourceJobs;

    const matched = sourceJobs.filter((job) => {
      const jobCode = String(job.job_code || '').toLowerCase();
      const positionTitle = String(job.position_title || '').toLowerCase();
      const city = String(job.city || '').toLowerCase();
      const country = String(job.country || '').toLowerCase();
      const location = `${city} ${country}`.trim();

      return (
        jobCode.includes(normalizedSearch) ||
        positionTitle.includes(normalizedSearch) ||
        location.includes(normalizedSearch)
      );
    });

    // Sort by numeric part of job code ascending: 4, 14, 24, ... 984
    return [...matched].sort((a, b) => {
      const aCode = String(a.job_code || '');
      const bCode = String(b.job_code || '');
      const aNum = Number((aCode.match(/\d+/g) || []).join(''));
      const bNum = Number((bCode.match(/\d+/g) || []).join(''));

      if (Number.isNaN(aNum) && Number.isNaN(bNum)) {
        return aCode.localeCompare(bCode);
      }
      if (Number.isNaN(aNum)) return 1;
      if (Number.isNaN(bNum)) return -1;
      if (aNum !== bNum) return aNum - bNum;
      return aCode.localeCompare(bCode);
    });
  }, [ceipalAllJobs, ceipalJobs, ceipalSearchTerm]);

  const effectiveCeipalTotalItems = isCeipalSearchActive ? filteredCeipalJobs.length : ceipalTotalJobs;
  const effectiveCeipalTotalPages = Math.max(1, Math.ceil(effectiveCeipalTotalItems / ceipalRecordsPerPage));

  const displayedCeipalJobs = useMemo(() => {
    if (!isCeipalSearchActive) return ceipalJobs;
    const start = (ceipalCurrentPage - 1) * ceipalRecordsPerPage;
    const end = start + ceipalRecordsPerPage;
    return filteredCeipalJobs.slice(start, end);
  }, [isCeipalSearchActive, ceipalJobs, filteredCeipalJobs, ceipalCurrentPage, ceipalRecordsPerPage]);

  // Apply used/new filter on top of the paginated display
  const filterAwareDisplayedJobs = useMemo(() => {
    if (ceipalJobFilter === 'all') return displayedCeipalJobs;
    return displayedCeipalJobs.filter(job => {
      const isUsed = usedJobCodes.has((job.job_code || '').toLowerCase().trim());
      return ceipalJobFilter === 'used' ? isUsed : !isUsed;
    });
  }, [displayedCeipalJobs, ceipalJobFilter, usedJobCodes]);

  // Pagination calculations for Ceipal modal
  const ceipalStartIndex = effectiveCeipalTotalItems === 0 ? 0 : (ceipalCurrentPage - 1) * ceipalRecordsPerPage + 1;
  const ceipalEndIndex = Math.min(ceipalCurrentPage * ceipalRecordsPerPage, effectiveCeipalTotalItems);

  // Pagination handlers for Ceipal modal
  const handleCeipalPageChange = (page) => {
    const pageNumber = Number(page?.selected ?? page);
    const maxPage = isCeipalSearchActive ? effectiveCeipalTotalPages : (ceipalTotalPages || 1);
    const safePage = Number.isNaN(pageNumber) ? 1 : Math.min(Math.max(pageNumber, 1), maxPage);
    if (safePage === ceipalCurrentPage) return;
    setCeipalCurrentPage(safePage);
    if (!isCeipalSearchActive) {
      handleImportFromCeipal(safePage, ceipalSearchTerm);
    }
  };

  const handleCeipalPageSizeChange = (size) => {
    const pageSize = Number(size);
    if (!pageSize || pageSize <= 0) return;
    setCeipalRecordsPerPage(pageSize);
    setCeipalCurrentPage(1); // Reset to first page when changing page size
    if (!isCeipalSearchActive) {
      ceipalPageCacheRef.current = {};
      handleImportFromCeipal(1, ceipalSearchTerm, pageSize); // Fetch first page using selected page size
    }
  };

  // Fetch all job codes already used in existing positions.
  // Called on mount so data is ready before the modal opens.
  const fetchUsedJobCodes = useCallback(async () => {
    setUsedJobCodesLoading(true);
    try {
      const resp = await api.get('/positions/used-job-codes');
      setUsedJobCodes(new Set((resp.data || []).map(c => (c || '').toLowerCase().trim())));
    } catch (_) { /* non-critical */ }
    finally { setUsedJobCodesLoading(false); }
  }, []);

  // Load used job codes immediately on page mount so the Ceipal modal always has them ready.
  useEffect(() => { fetchUsedJobCodes(); }, [fetchUsedJobCodes]);

  // Reset pagination when modal opens
  const handleCeipalModalOpen = () => {
    setCeipalCurrentPage(1);
    setCeipalSearchTerm('');
    setCeipalJobFilter('all');
    handleImportFromCeipal(1, ''); // Load first page without search
    fetchAllCeipalJobs(); // Preload full dataset so search is immediate and comprehensive
    fetchUsedJobCodes(); // Refresh used codes in case positions were created since page load
  };

  useEffect(() => {
    if (ceipalCurrentPage > effectiveCeipalTotalPages) {
      setCeipalCurrentPage(1);
    }
  }, [ceipalCurrentPage, effectiveCeipalTotalPages]);

  const jobTitleLimitModal =
    typeof document !== 'undefined'
      ? createPortal(
          <Modal
            show={jobTitleLimitModalOpen}
            onHide={() => setJobTitleLimitModalOpen(false)}
            centered
            backdrop="static"
            className="job-title-limit-modal-root"
            enforceFocus={false}
            restoreFocus={false}
          >
            <Modal.Body className="text-center py-4">
              <p className="mb-0 fw-semibold">{JOB_TITLE_EXCEED_MSG}</p>
            </Modal.Body>
            <Modal.Footer className="justify-content-center border-0 pt-0">
              <Button variant="primary" onClick={() => setJobTitleLimitModalOpen(false)}>
                OK
              </Button>
            </Modal.Footer>
          </Modal>,
          document.body
        )
      : null;

  // List view — positions table as landing
  if (view === 'list') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--ds-bg)', padding: '24px 0' }}>
        <Container fluid="lg">
          <PositionsTable mode="craftJD" onEnterWizard={enterWizardForPosition} />
        </Container>
      </div>
    );
  }

  return (
    <>
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      padding: '20px 0'
    }}>
      <Container fluid="lg">

        {/* Back to positions list */}
        <div className="mb-2">
          <button
            onClick={() => setView('list')}
            style={{
              background: 'none', border: 'none', color: '#0041C2',
              cursor: 'pointer', fontSize: '0.85rem', padding: 0, fontWeight: 600,
            }}
          >
            ← Back to positions
          </button>
        </div>

        {/* Vertical Step Progress Indicator */}
        <Card className="border-0 shadow-sm mb-2" style={{
          borderRadius: '6px',
          overflow: 'hidden',
          background: '#ffffff',
          border: '1px solid rgba(0,0,0,0.05)'
        }}>
          <Card.Body className="py-3 px-3">
            <Row className="g-3">
              {/* Step 1 - Vertical Layout */}
              <Col md={4} xs={12}>
                <div className="d-flex align-items-center">
                  <div className="position-relative me-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle transition-all text-white shadow-sm" style={{
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.3s ease',
                      transform: positionId ? 'scale(1.05)' : 'scale(1)',
                      backgroundColor: positionId ? '#28a745' : 'rgb(89, 144, 225)'
                    }}>
                      {positionId ? <CheckCircle2 size={16} /> : <span className="fw-bold" style={{ fontSize: '14px' }}>1</span>}
                    </div>
                    {positionId && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <Badge bg="success" pill className="animate-pulse" style={{ fontSize: '8px', padding: '2px 4px' }}>
                          <Check size={8} />
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0" style={{
                      fontSize: '16px',
                      color: positionId ? '#28a745' : 'rgb(89, 144, 225)'
                    }}>
                      Create Position
                    </h6>
                  </div>
                </div>
              </Col>

              {/* Step 2 - Vertical Layout */}
              <Col md={4} xs={12}>
                <div className="d-flex align-items-center">
                  <div className="position-relative me-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle transition-all shadow-sm" style={{
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.3s ease',
                      transform: (jdPanelOpen || showJdPreview) ? 'scale(1.05)' : 'scale(1)',
                      backgroundColor: showJdPreview && (generatedJd || uploadedJd) ? '#28a745' :
                        jdPanelOpen ? 'rgb(89, 144, 225)' : '#f8f9fa',
                      color: (jdPanelOpen || showJdPreview) ? 'white' : '#6c757d'
                    }}>
                      {showJdPreview && (generatedJd || uploadedJd) ? <CheckCircle2 size={16} /> : <span className="fw-bold" style={{ fontSize: '14px' }}>2</span>}
                    </div>
                    {showJdPreview && (generatedJd || uploadedJd) && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <Badge bg="success" pill className="animate-pulse" style={{ fontSize: '8px', padding: '2px 4px' }}>
                          <Check size={8} />
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0" style={{
                      fontSize: '16px',
                      color: showJdPreview && (generatedJd || uploadedJd) ? '#28a745' :
                        jdPanelOpen ? 'rgb(89, 144, 225)' : '#6c757d'
                    }}>
                      Generate/Upload JD
                    </h6>
                  </div>
                </div>
              </Col>

              {/* Step 3 - Vertical Layout */}
              <Col md={4} xs={12}>
                <div className="d-flex align-items-center">
                  <div className="position-relative me-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle transition-all shadow-sm" style={{
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.3s ease',
                      transform: showJdPreview && (generatedJd || uploadedJd) ? 'scale(1.05)' : 'scale(1)',
                      backgroundColor: jdAttached ? '#28a745' :
                        showJdPreview && (generatedJd || uploadedJd) ? '#28a745' : '#f8f9fa',
                      color: showJdPreview && (generatedJd || uploadedJd) ? 'white' : '#6c757d'
                    }}>
                      {jdAttached ? <CheckCircle2 size={16} /> : <span className="fw-bold" style={{ fontSize: '14px' }}>3</span>}
                    </div>
                    {showJdPreview && (generatedJd || uploadedJd) && (
                      <div className="position-absolute top-0 start-50 translate-middle">
                        <Badge bg="success" pill className="animate-pulse" style={{ fontSize: '8px', padding: '2px 4px' }}>
                          <Zap size={8} />
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div>
                    <h6 className="fw-bold mb-0" style={{
                      fontSize: '16px',
                      color: showJdPreview && (generatedJd || uploadedJd) ? '#28a745' : '#6c757d'
                    }}>
                      Review & Finalize
                    </h6>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Progress Bar */}
            <div className="mt-3">
              <ProgressBar
                now={positionId ? (showJdPreview ? 100 : 50) : 0}
                style={{
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: 'rgb(89, 144, 225)'
                }}
                className="shadow-sm"
              />
            </div>
          </Card.Body>
        </Card>

        <Row className="g-4 mt-2 align-items-stretch">
          {/* Left Column - Enhanced Create Position */}
          <Col lg={4} md={12}>
            <Card className="border-0 shadow-sm h-100" style={{
              position: 'sticky',
              top: 'calc(var(--app-navbar-height, 82px) + 20px)',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#f8f9fa',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div className="card-header" style={{
                background: '#8e9aaf',
                border: 'none',
                padding: '0.75rem 0.5rem'
              }}>
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                    style={{ width: '40px', height: '40px' }}>
                    <Briefcase className="text-primary" size={20} />
                  </div>
                  <div>
                    <p className="text-white mb-0 fw-bold" style={{ fontSize: '18px' }}>Create Position</p>

                  </div>
                </div>
              </div>
              <Card.Body className="p-3">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-2" style={{ fontSize: '14px', color: '#000000' }}>
                      <Target className="text-dark" size={14} />
                      Position Name
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Briefcase className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={14} />
                      <Form.Control
                        type="text"
                        placeholder="e.g., Senior Software Engineer"
                        value={positionName}
                        isInvalid={
                          (createTouched || String(positionName || '').length > 0) &&
                          !!(createErrors.positionName || validatePositionNameField(positionName))
                        }
                        isValid={
                          !(createErrors.positionName || validatePositionNameField(positionName)) &&
                          hasMeaningfulText(positionName)
                        }
                        maxLength={POSITION_NAME_MAX_CHARS}
                        onChange={(e) => {
                          if (positionId) return;
                          const v = e.target.value;
                          setPositionName(v);
                          const show = createTouched || String(v).length > 0;
                          setCreateErrors((prev) => ({
                            ...prev,
                            positionName: show ? validatePositionNameField(v) : undefined
                          }));
                        }}
                        disabled={loading || !!positionId}
                        readOnly={!!positionId}
                        className="ps-4 border-2 only-one-validation-icon"
                        style={{
                          borderRadius: '6px',
                          borderColor:
                            !(createErrors.positionName || validatePositionNameField(positionName)) && hasMeaningfulText(positionName)
                              ? '#198754'
                              : (createTouched || String(positionName || '').length > 0) && !!(createErrors.positionName || validatePositionNameField(positionName))
                              ? '#dc3545'
                              : '#dee2e6',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                          padding: '0.5rem 0.75rem'
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {createErrors.positionName || validatePositionNameField(positionName)}
                      </Form.Control.Feedback>
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        {renderFieldStatusIcon({
                          isInvalid:
                            (createTouched || String(positionName || '').length > 0) &&
                            !!(createErrors.positionName || validatePositionNameField(positionName)),
                          isValid:
                            !(createErrors.positionName || validatePositionNameField(positionName)) &&
                            hasMeaningfulText(positionName),
                          size: 14
                        })}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-1">
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        {String(positionName || '').length}/{POSITION_NAME_MAX_CHARS}
                      </small>
                    </div>

                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-2" style={{ fontSize: '14px', color: '#000000' }}>
                      <Briefcase className="text-dark" size={14} />
                      Job Family Profile
                    </Form.Label>
                    <JobFamilyTripleSelect
                      roleKey={selectedJobFamilyKey}
                      preview={jobFamilyPreview}
                      idPrefix="op-jf"
                      onRoleKeyChange={(key) => {
                        jobFamilyTouchedRef.current = true;
                        setJobFamilyPreview(null);
                        setSelectedJobFamilyKey(key);
                        if (positionId) setPositionJobFamilyKey(positionId, key);
                      }}
                    />
                    <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                      After attaching the JD, you will configure skill weightage for this profile in Stack Ranking.
                    </small>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-2" style={{ fontSize: '14px', color: '#000000' }}>
                      <Building2 className="text-dark" size={14} />
                      Client Name
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Building2 className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={14} />
                      <Form.Control
                        type="text"
                        placeholder="e.g., ABC Corporation"
                        value={clientName}
                        isInvalid={
                          (createTouched || String(clientName || '').length > 0) &&
                          !!(createErrors.clientName || validateClientNameField(clientName))
                        }
                        isValid={
                          !(createErrors.clientName || validateClientNameField(clientName)) &&
                          hasMeaningfulText(clientName)
                        }
                        maxLength={CLIENT_NAME_MAX_CHARS}
                        onChange={(e) => {
                          if (positionId) return;
                          const v = e.target.value;
                          setClientName(v);
                          const show = createTouched || String(v).length > 0;
                          setCreateErrors((prev) => ({
                            ...prev,
                            clientName: show ? validateClientNameField(v) : undefined
                          }));
                        }}
                        disabled={loading || !!positionId}
                        readOnly={!!positionId}
                        className="ps-4 border-2 only-one-validation-icon"
                        style={{
                          borderRadius: '6px',
                          borderColor:
                            !(createErrors.clientName || validateClientNameField(clientName)) && hasMeaningfulText(clientName)
                              ? '#198754'
                              : (createTouched || String(clientName || '').length > 0) && !!(createErrors.clientName || validateClientNameField(clientName))
                              ? '#dc3545'
                              : '#dee2e6',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                          padding: '0.5rem 0.75rem'
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {createErrors.clientName || validateClientNameField(clientName)}
                      </Form.Control.Feedback>
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        {renderFieldStatusIcon({
                          isInvalid:
                            (createTouched || String(clientName || '').length > 0) &&
                            !!(createErrors.clientName || validateClientNameField(clientName)),
                          isValid:
                            !(createErrors.clientName || validateClientNameField(clientName)) &&
                            hasMeaningfulText(clientName),
                          size: 14
                        })}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-1">
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        {String(clientName || '').length}/{CLIENT_NAME_MAX_CHARS}
                      </small>
                    </div>

                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-2" style={{ fontSize: '14px', color: '#000000' }}>
                      <Database className="text-dark" size={14} />
                      Job Code
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Database className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={14} />
                      <Form.Control
                        type="text"
                        placeholder="e.g., JPC-775"
                        value={jobCode}
                        isInvalid={
                          (createTouched || String(jobCode || '').trim().length > 0) &&
                          !!(createErrors.jobCode || validateJobCodeField(jobCode))
                        }
                        isValid={
                          String(jobCode || '').trim().length > 0 &&
                          !(createErrors.jobCode || validateJobCodeField(jobCode))
                        }
                        maxLength={JOB_CODE_MAX_CHARS}
                        onChange={(e) => {
                          if (positionId || selectedCeipalJob) return;
                          const v = e.target.value;
                          setJobCode(v);
                          const trimmed = String(v ?? '').trim();
                          const show = createTouched || trimmed.length > 0;
                          setCreateErrors((prev) => ({
                            ...prev,
                            jobCode: show ? validateJobCodeField(v) : undefined,
                          }));
                        }}
                        disabled={loading || !!positionId}
                        readOnly={!!positionId || !!selectedCeipalJob}
                        className="ps-4 border-2 only-one-validation-icon"
                        style={{
                          borderRadius: '6px',
                          borderColor:
                            String(jobCode || '').trim().length > 0 && !(createErrors.jobCode || validateJobCodeField(jobCode))
                              ? '#198754'
                              : (createTouched || String(jobCode || '').trim().length > 0) && !!(createErrors.jobCode || validateJobCodeField(jobCode))
                              ? '#dc3545'
                              : '#dee2e6',
                          transition: 'all 0.3s ease',
                          fontSize: '14px',
                          padding: '0.5rem 0.75rem'
                        }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {createErrors.jobCode || validateJobCodeField(jobCode)}
                      </Form.Control.Feedback>
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        {renderFieldStatusIcon({
                          isInvalid:
                            (createTouched || String(jobCode || '').trim().length > 0) &&
                            !!(createErrors.jobCode || validateJobCodeField(jobCode)),
                          isValid:
                            String(jobCode || '').trim().length > 0 &&
                            !(createErrors.jobCode || validateJobCodeField(jobCode)),
                          size: 14
                        })}
                      </div>
                    </div>
                  </Form.Group>

                  <Button
                    type="button"
                    onClick={(e) => {
                      e?.preventDefault?.();
                      handleCreatePosition();
                    }}
                    disabled={loading || !!positionId || jdPanelOpen}
                    className="w-100 d-flex align-items-center justify-content-center gap-2 py-2 mb-2"
                    style={{
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      transform: loading ? 'scale(0.98)' : 'scale(1)',
                      backgroundColor: 'rgb(89, 144, 225)',
                      borderColor: 'rgb(89, 144, 225)',
                      color: 'white'
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Creating...</span>
                      </>
                    ) : positionId ? (
                      <>
                        <CheckCircle2 size={16} />
                        <span>Position Created</span>
                      </>
                    ) : jdPanelOpen ? (
                      <>
                        <CheckCircle2 size={16} />
                        <span>Details Confirmed</span>
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        <span>Create Position</span>
                      </>
                    )}
                  </Button>

                  {!positionId && !jdPanelOpen && (
                    <>
                      {/* OR divider */}
                      <div className="d-flex align-items-center my-2 gap-2">
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                        <span className="text-muted fw-semibold" style={{ fontSize: '12px' }}>OR</span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#dee2e6' }} />
                      </div>
                      <Button
                        type="button"
                        onClick={(e) => {
                          e?.preventDefault?.();
                          if (positionId) return;
                          handleImportFromCeipal(1, '');
                        }}
                        disabled={ceipalLoading}
                        variant="outline-primary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          transition: 'all 0.3s ease',
                          transform: ceipalLoading ? 'scale(0.98)' : 'scale(1)',
                          borderColor: 'rgb(89, 144, 225)',
                          color: 'rgb(89, 144, 225)',
                          backgroundColor: 'transparent'
                        }}
                      >
                        {ceipalLoading ? (
                          <>
                            <Spinner animation="border" size="sm" />
                            <span>Loading...</span>
                          </>
                        ) : (
                          <>
                            <Database size={16} />
                            <span>Import from Ceipal</span>
                          </>
                        )}
                      </Button>

                      {/* Workday import — coming soon (greyed out, non-clickable) */}
                      <Button
                        type="button"
                        disabled
                        variant="outline-secondary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          borderColor: '#ced4da',
                          color: '#adb5bd',
                          backgroundColor: 'transparent',
                          cursor: 'not-allowed',
                          opacity: 0.55,
                        }}
                      >
                        <Database size={16} />
                        <span>Import from Workday</span>
                        <span style={{ fontSize: '10px', fontWeight: '500', marginLeft: '4px', backgroundColor: '#e9ecef', color: '#6c757d', borderRadius: '4px', padding: '1px 6px' }}>Coming Soon</span>
                      </Button>

                      {/* Greenhouse import — coming soon (greyed out, non-clickable) */}
                      <Button
                        type="button"
                        disabled
                        variant="outline-secondary"
                        className="w-100 d-flex align-items-center justify-content-center gap-2 py-2"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '14px',
                          borderColor: '#ced4da',
                          color: '#adb5bd',
                          backgroundColor: 'transparent',
                          cursor: 'not-allowed',
                          opacity: 0.55,
                        }}
                      >
                        <Database size={16} />
                        <span>Import from Greenhouse</span>
                        <span style={{ fontSize: '10px', fontWeight: '500', marginLeft: '4px', backgroundColor: '#e9ecef', color: '#6c757d', borderRadius: '4px', padding: '1px 6px' }}>Coming Soon</span>
                      </Button>
                    </>
                  )}
                </Form>

                {jdPanelOpen && !positionId && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <CheckCircle2 size={14} className="text-success" />
                      <p className="fw-bold text-dark mb-0" style={{ fontSize: '13px' }}>Position details confirmed — add your JD</p>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setJdPanelOpen(false)}
                      className="p-0 text-muted"
                      style={{ fontSize: '12px', textDecoration: 'underline' }}
                    >
                      Edit position details
                    </Button>
                  </div>
                )}

                {positionId && (
                  <div className="mt-3">
                    <p className="fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>Position Created Successfully!</p>
                    <p className="mb-0 text-dark" style={{ fontSize: '12px' }}>
                      You can now generate or upload a job description for this position.
                    </p>
                  </div>
                )}

                {(positionId || jdAttached) && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleCreateNewPosition}
                    disabled={loading || uploadLoading || jdGenerationLoading}
                    className="w-100 mt-2 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: '8px',
                      fontWeight: '500',
                      borderWidth: '2px',
                      borderColor: 'rgb(89, 144, 225)',
                      color: 'rgb(89, 144, 225)',
                      backgroundColor: 'transparent',
                      fontSize: '13px'
                    }}
                  >
                    <RotateCcw size={14} />
                    <span>Create New Position</span>
                  </Button>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Step Content */}
          <Col lg={8} md={12}>
            {!positionId && !jdPanelOpen ? (
              /* Show database (current positions) when no active creation flow */
              <div className="h-100" style={{ display: 'flex', flexDirection: 'column' }}>
                <DatabasePage embedded={true} />
              </div>
            ) : (positionId || jdPanelOpen) && jdImportLoading && !generatedJd && !uploadedJd ? (
              <Card className="border-0 shadow-sm text-center h-100" style={{
                borderRadius: '8px',
                background: '#f8f9fa',
                minHeight: '300px'
              }}>
                <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                  <Spinner animation="border" style={{ color: 'rgb(89, 144, 225)', width: '2.5rem', height: '2.5rem' }} />
                  <p className="fw-bold text-dark mt-3 mb-1" style={{ fontSize: '15px' }}>Importing JD from Ceipal...</p>
                  <p className="text-muted mb-0" style={{ fontSize: '12px' }}>Fetching and saving the job description</p>
                </Card.Body>
              </Card>
            ) : (positionId || jdPanelOpen) && (!showJdPreview || (!generatedJd && !uploadedJd)) ? (
              <div>
                {/* Enhanced Tab Switcher */}
                <Card className="border-0 shadow-sm mb-2" style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#f8f9fa',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <Card.Body className="p-1">
                    <div className="row g-1">
                      <div className={positionCreationSource === 'manual' ? 'col-6' : 'col-4'}>
                        <Button
                          onClick={() => setActiveTab('structured')}
                          variant="outline-secondary"
                          className={`w-100 d-flex align-items-center py-2 ${activeTab === 'structured' ? 'shadow-sm' : ''
                            }`}
                          style={{
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '12px',
                            transition: 'all 0.3s ease',
                            transform: activeTab === 'structured' ? 'scale(1.01)' : 'scale(1)',
                            backgroundColor: activeTab === 'structured' ? '#8e9aaf' : 'transparent',
                            borderColor: '#8e9aaf',
                            color: activeTab === 'structured' ? 'white' : '#8e9aaf',
                            padding: '0.5rem 0.25rem'
                          }}
                        >
                          <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center me-1"
                            style={{ width: '30px', height: '30px' }}>
                            <Sparkles className={activeTab === 'structured' ? 'text-primary' : 'text-primary'} size={16} />
                          </div>
                          <div>
                            <p className="mb-0 fw-bold" style={{ fontSize: '14px', color: activeTab === 'structured' ? 'white' : '#000000' }}>Generate JD</p>
                            <small className="opacity-75" style={{ fontSize: '10px', color: activeTab === 'structured' ? 'white' : '#000000' }}>AI powered</small>
                          </div>
                        </Button>
                      </div>
                      <div className={positionCreationSource === 'manual' ? 'col-6' : 'col-4'}>
                        <Button
                          onClick={() => setActiveTab('upload')}
                          variant="outline-secondary"
                          className={`w-100 d-flex align-items-center py-2 ${activeTab === 'upload' ? 'shadow-sm' : ''
                            }`}
                          style={{
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '12px',
                            transition: 'all 0.3s ease',
                            transform: activeTab === 'upload' ? 'scale(1.01)' : 'scale(1)',
                            backgroundColor: activeTab === 'upload' ? '#8e9aaf' : 'transparent',
                            borderColor: '#8e9aaf',
                            color: activeTab === 'upload' ? 'white' : '#8e9aaf',
                            padding: '0.5rem 0.25rem'
                          }}
                        >
                          <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center me-1"
                            style={{ width: '30px', height: '30px' }}>
                            <Upload className={activeTab === 'upload' ? 'text-primary' : 'text-primary'} size={16} />
                          </div>
                          <div>
                            <p className="mb-0 fw-bold" style={{ fontSize: '14px', color: activeTab === 'upload' ? 'white' : '#000000' }}>Upload JD</p>
                            <small className="opacity-75" style={{ fontSize: '10px', color: activeTab === 'upload' ? 'white' : '#000000' }}>from client</small>
                          </div>
                        </Button>
                      </div>
                      {positionCreationSource !== 'manual' && (
                        <div className="col-4">
                          <Button
                            onClick={() => setActiveTab('ceipal')}
                            variant="outline-secondary"
                            className={`w-100 d-flex align-items-center py-2 ${activeTab === 'ceipal' ? 'shadow-sm' : ''
                              }`}
                            style={{
                              borderRadius: '6px',
                              fontWeight: '600',
                              fontSize: '12px',
                              transition: 'all 0.3s ease',
                              transform: activeTab === 'ceipal' ? 'scale(1.01)' : 'scale(1)',
                              backgroundColor: activeTab === 'ceipal' ? '#8e9aaf' : 'transparent',
                              borderColor: '#8e9aaf',
                              color: activeTab === 'ceipal' ? 'white' : '#8e9aaf',
                              padding: '0.5rem 0.25rem'
                            }}
                          >
                            <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center me-1"
                              style={{ width: '30px', height: '30px' }}>
                              <Database className={activeTab === 'ceipal' ? 'text-primary' : 'text-primary'} size={16} />
                            </div>
                            <div>
                              <p className="mb-0 fw-bold" style={{ fontSize: '14px', color: activeTab === 'ceipal' ? 'white' : '#000000' }}>Ceipal JD</p>
                              <small className="opacity-75" style={{ fontSize: '10px', color: activeTab === 'ceipal' ? 'white' : '#000000' }}>import job</small>
                            </div>
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>

                {/* Enhanced Generate Tab */}
                {activeTab === 'structured' && (
                  <Card className="border-0 shadow-sm" style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#f8f9fa',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Card.Body className="p-3">
                      <Form>
                        <Form.Group className="mb-2">
                          <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                            <Briefcase className="text-dark" size={14} />
                            Job Title/Role
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <div className="position-relative">
                            <Briefcase className="position-absolute top-50 start-0 translate-middle-y ms-1 text-muted" size={12} />
                            <input
                              type="text"
                              id="openpositions-job-title-role"
                              name="jobTitleRole"
                              autoComplete="off"
                              placeholder="e.g., Full Stack Developer"
                              value={structuredData.job_role}
                              maxLength={JOB_TITLE_MAX_CHARS}
                              aria-invalid={
                                (structuredTouched || String(structuredData.job_role || '').length > 0) &&
                                !!validateJobTitle(structuredData.job_role)
                              }
                              className={`form-control ps-3 border-2 only-one-validation-icon ${
                                (structuredTouched || String(structuredData.job_role || '').length > 0) &&
                                validateJobTitle(structuredData.job_role)
                                  ? 'is-invalid'
                                  : ''
                              }`}
                              onBeforeInput={(e) => {
                                const ev = e.nativeEvent;
                                if (!ev || typeof ev.inputType !== 'string') return;
                                if (
                                  ev.inputType.startsWith('delete') ||
                                  ev.inputType === 'historyUndo' ||
                                  ev.inputType === 'historyRedo'
                                ) {
                                  return;
                                }
                                const el = e.currentTarget;
                                // Use live DOM value — React state can lag one frame behind typing/paste.
                                const cur = el.value;
                                const start = typeof el.selectionStart === 'number' ? el.selectionStart : 0;
                                const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
                                let insert = typeof ev.data === 'string' ? ev.data : '';
                                // Chrome/Firefox often leave data null for insertFromPaste; read clipboard here.
                                if (
                                  (!insert || insert.length === 0) &&
                                  ev.inputType === 'insertFromPaste'
                                ) {
                                  try {
                                    insert =
                                      (e.clipboardData || ev.clipboardData || window.clipboardData)?.getData(
                                        'text/plain'
                                      ) || '';
                                  } catch {
                                    insert = '';
                                  }
                                }
                                if (insert == null || insert === '') return;
                                const nextLen = cur.length - (end - start) + insert.length;
                                if (nextLen > JOB_TITLE_MAX_CHARS) {
                                  e.preventDefault();
                                  notifyJobTitleLengthExceeded();
                                }
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.length > JOB_TITLE_MAX_CHARS) {
                                  notifyJobTitleLengthExceeded();
                                }
                                const value = clampJobTitleRole(raw);
                                setStructuredData((prev) => ({ ...prev, job_role: value }));
                                const showErr = structuredTouched || String(value).length > 0;
                                const err = showErr ? validateJobTitle(value) : undefined;
                                setStructuredErrors((prev) => ({
                                  ...prev,
                                  job_role: err
                                }));
                                const exceeded = err === JOB_TITLE_EXCEED_MSG;
                                if (exceeded && !prevJobTitleExceededRef.current) {
                                  notifyJobTitleLengthExceeded();
                                }
                                prevJobTitleExceededRef.current = exceeded;
                              }}
                              onBlur={(e) => {
                                const v = clampJobTitleRole(e.target.value);
                                if (validateJobTitle(v) === JOB_TITLE_EXCEED_MSG) {
                                  notifyJobTitleLengthExceeded();
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
                                if (['Backspace', 'Delete', 'Tab', 'Enter', 'Escape'].includes(e.key)) return;
                                const el = e.currentTarget;
                                const start = el.selectionStart ?? 0;
                                const end = el.selectionEnd ?? 0;
                                const cur = el.value;
                                if (end > start) return;
                                if (cur.length >= JOB_TITLE_MAX_CHARS) {
                                  e.preventDefault();
                                  notifyJobTitleLengthExceeded();
                                }
                              }}
                              onPaste={(e) => {
                                // Always handle paste in JS so we reliably detect overflow (browser maxLength can hide it).
                                e.preventDefault();
                                const paste =
                                  (e.clipboardData || window.clipboardData)?.getData('text/plain') || '';
                                const el = e.currentTarget;
                                const cur = el.value;
                                const start = typeof el.selectionStart === 'number' ? el.selectionStart : cur.length;
                                const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
                                const room = JOB_TITLE_MAX_CHARS - (cur.length - (end - start));
                                const take = Math.min(paste.length, Math.max(0, room));
                                if (paste.length > room) {
                                  notifyJobTitleLengthExceeded();
                                }
                                const inserted = paste.slice(0, take);
                                const newVal = cur.slice(0, start) + inserted + cur.slice(end);
                                setStructuredData((prev) => ({ ...prev, job_role: newVal }));
                                const err = validateJobTitle(newVal);
                                setStructuredErrors((prev) => ({
                                  ...prev,
                                  job_role: structuredTouched || newVal.length > 0 ? err : undefined
                                }));
                                if (err === JOB_TITLE_EXCEED_MSG) {
                                  notifyJobTitleLengthExceeded();
                                }
                                prevJobTitleExceededRef.current = err === JOB_TITLE_EXCEED_MSG;
                                requestAnimationFrame(() => {
                                  try {
                                    el.setSelectionRange(start + inserted.length, start + inserted.length);
                                  } catch (_) {
                                    /* ignore */
                                  }
                                });
                              }}
                              style={{
                                borderRadius: '4px',
                                borderColor: '#dee2e6',
                                transition: 'all 0.3s ease',
                                fontSize: '12px',
                                padding: '0.375rem 0.5rem'
                              }}
                            />
                            {(structuredTouched || String(structuredData.job_role || '').length > 0) &&
                            validateJobTitle(structuredData.job_role) &&
                            validateJobTitle(structuredData.job_role) !== JOB_TITLE_EXCEED_MSG ? (
                              <div className="invalid-feedback d-block">
                                {validateJobTitle(structuredData.job_role)}
                              </div>
                            ) : null}
                            <div className="position-absolute top-50 end-0 translate-middle-y me-1">
                              {renderFieldStatusIcon({
                                isInvalid:
                                  (structuredTouched || String(structuredData.job_role || '').length > 0) &&
                                  !!validateJobTitle(structuredData.job_role),
                                isValid:
                                  !validateJobTitle(structuredData.job_role) &&
                                  hasMeaningfulText(structuredData.job_role),
                                size: 14
                              })}
                            </div>
                          </div>
                          <div className="d-flex justify-content-end mt-1">
                            <small
                              className={
                                String(structuredData.job_role || '').length >= JOB_TITLE_MAX_CHARS
                                  ? 'text-danger'
                                  : String(structuredData.job_role || '').length >= JOB_TITLE_WARN_CHARS
                                    ? 'text-warning'
                                    : 'text-muted'
                              }
                              style={{ fontSize: '11px' }}
                            >
                              {String(structuredData.job_role || '').length}/{JOB_TITLE_MAX_CHARS}
                            </small>
                          </div>

                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                                <Calendar className="text-dark" size={12} />
                                Experience (Years)
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="position-relative">
                                <Calendar className="position-absolute top-50 start-0 translate-middle-y ms-1 text-muted" size={12} />
                                <Form.Control
                                  type="text"
                                  placeholder="e.g.:5"
                                  value={structuredData.experience}
                                  isInvalid={structuredTouched && !!structuredErrors.experience}
                                  onChange={(e) => {
                                    const nextValue = sanitizeExperienceInput(e.target.value);
                                    setStructuredData((prev) => ({ ...prev, experience: nextValue }));
                                    if (structuredTouched) {
                                      const err = validateExperienceField(nextValue);
                                      setStructuredErrors((prev) => ({
                                        ...prev,
                                        experience: err
                                      }));
                                    }
                                  }}
                                  className="ps-3 border-2 only-one-validation-icon"
                                  inputMode="text"
                                  onKeyDown={(e) => {
                                    if (e.key !== '-' && e.key !== 'Minus') return;
                                    const el = e.currentTarget;
                                    const val = String(el.value ?? '');
                                    const pos = el.selectionStart ?? 0;
                                    const beforeCursor = val.slice(0, pos);
                                    // Allow "-" only as range separator (e.g. 1-3), never as a negative sign.
                                    if (!/\d/.test(beforeCursor) || val.includes('-')) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const paste = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                                    const el = e.currentTarget;
                                    const cur = String(el.value ?? '');
                                    const start = el.selectionStart ?? cur.length;
                                    const end = el.selectionEnd ?? start;
                                    const next = sanitizeExperienceInput(
                                      cur.slice(0, start) + paste + cur.slice(end)
                                    );
                                    el.value = next;
                                    el.dispatchEvent(new Event('input', { bubbles: true }));
                                  }}
                                  style={{
                                    borderRadius: '4px',
                                    borderColor: '#dee2e6',
                                    transition: 'all 0.3s ease',
                                    fontSize: '12px',
                                    padding: '0.375rem 0.5rem'
                                  }}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {structuredErrors.experience}
                                </Form.Control.Feedback>
                                <div className="position-absolute top-50 end-0 translate-middle-y me-1">
                                  {renderFieldStatusIcon({
                                    isInvalid: structuredTouched && !!structuredErrors.experience,
                                    isValid: !!String(structuredData.experience ?? '').trim() && !(structuredTouched && !!structuredErrors.experience),
                                    size: 12
                                  })}
                                </div>
                              </div>

                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                                <MapPin className="text-dark" size={14} />
                                Location
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <div className="position-relative">
                                <MapPin className="position-absolute top-50 start-0 translate-middle-y ms-1 text-muted" size={12} />
                                <Form.Control
                                  type="text"
                                  placeholder="e.g., Hyderabad, India"
                                  value={structuredData.location}
                                  isInvalid={structuredTouched && !!structuredErrors.location}
                                  onChange={(e) => {
                                    const value = sanitizeLocationInput(e.target.value);
                                    setStructuredData((prev) => ({ ...prev, location: value }));
                                    if (structuredTouched) {
                                      const err = validateWorkLocationField(value);
                                      setStructuredErrors((prev) => ({
                                        ...prev,
                                        location: err
                                      }));
                                    }
                                  }}
                                  className="ps-3 border-2 only-one-validation-icon"
                                  style={{
                                    borderRadius: '4px',
                                    borderColor: '#dee2e6',
                                    transition: 'all 0.3s ease',
                                    fontSize: '12px',
                                    padding: '0.375rem 0.5rem'
                                  }}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {structuredErrors.location}
                                </Form.Control.Feedback>
                                <div className="position-absolute top-50 end-0 translate-middle-y me-1">
                                  {renderFieldStatusIcon({
                                    isInvalid: structuredTouched && !!structuredErrors.location,
                                    isValid: isValidLocationField(structuredData.location),
                                    size: 12
                                  })}
                                </div>
                              </div>

                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-2">
                          <Col xs={12} md={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                                Company Name
                                <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>(optional)</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="e.g. Feuji Software Solutions Pvt Ltd."
                                value={structuredData.company_name}
                                maxLength={200}
                                onChange={(e) => setStructuredData((prev) => ({ ...prev, company_name: e.target.value }))}
                                className="border-2"
                                style={{ borderRadius: '4px', borderColor: '#dee2e6', fontSize: '12px', padding: '0.375rem 0.5rem' }}
                              />
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group>
                              <Form.Label className="fw-bold mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                                Position
                              </Form.Label>
                              <Form.Select
                                value={structuredData.position_type}
                                onChange={(e) => setStructuredData((prev) => ({ ...prev, position_type: e.target.value }))}
                                className="border-2"
                                style={{ borderRadius: '4px', borderColor: '#dee2e6', fontSize: '12px', padding: '0.375rem 0.5rem' }}
                              >
                                <option value="Permanent Position">Permanent Position</option>
                                <option value="Contract Position">Contract Position</option>
                                <option value="Intern">Intern</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-2">
                          <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                            About The Company
                            <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>(optional)</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter a short description of the company — its mission, size, industry, and culture..."
                            value={structuredData.about_company}
                            maxLength={3000}
                            onChange={(e) => setStructuredData((prev) => ({ ...prev, about_company: e.target.value }))}
                            className="border-2"
                            style={{ borderRadius: '4px', borderColor: '#dee2e6', fontSize: '12px', resize: 'vertical' }}
                          />
                          <div className="d-flex justify-content-end mt-1">
                            <small className="text-muted" style={{ fontSize: '11px' }}>
                              {(structuredData.about_company || '').length} / 3000
                            </small>
                          </div>
                        </Form.Group>

                        <div className="position-relative my-2">
                          <hr style={{ borderColor: '#e9ecef', borderWidth: '1px' }} />
                          <div className="position-absolute top-50 start-50 translate-middle bg-white px-2">
                            <Badge bg="light" text="dark" className="px-2 py-1" style={{ fontSize: '12px' }}>
                              <span className="fw-bold">OR</span>
                            </Badge>
                          </div>
                        </div>

                        <Form.Group className="mb-2">
                          <Form.Label className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '14px', color: '#000000' }}>
                            <FileText className="text-dark" size={14} />
                            Quick Input

                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Paste job description, requirements, or any details here..."
                            value={singleInput}
                            isInvalid={quickInputTouched && !!validateQuickInput(singleInput)}
                            maxLength={QUICK_INPUT_MAX_CHARS}
                            onChange={(e) => setSingleInput(e.target.value)}
                            onBlur={() => {
                              if (String(singleInput || '').trim()) setQuickInputTouched(true);
                            }}
                            className="border-2"
                            style={{
                              borderRadius: '4px',
                              borderColor: '#dee2e6',
                              transition: 'all 0.3s ease',
                              resize: 'vertical',
                              fontSize: '12px'
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {validateQuickInput(singleInput)}
                          </Form.Control.Feedback>
                          <div className="d-flex justify-content-end mt-1">
                            <small
                              className={
                                String(singleInput || '').length > QUICK_INPUT_MAX_CHARS
                                  ? 'text-danger'
                                  : 'text-muted'
                              }
                              style={{ fontSize: '11px' }}
                            >
                              {String(singleInput || '').length.toLocaleString()}/
                              {QUICK_INPUT_MAX_CHARS.toLocaleString()}
                            </small>
                          </div>

                        </Form.Group>

                        {!aiJdOn && (
                          <div className="text-muted mb-2" style={{ fontSize: '12px' }}>{AI_JD_OFF_MESSAGE}</div>
                        )}
                        <Button
                          onClick={handleGenerateClick}
                          disabled={jdGenerationLoading || !aiJdOn}
                          title={aiJdOn ? undefined : AI_JD_OFF_MESSAGE}
                          className="w-100 d-flex align-items-center justify-content-center gap-1 py-1"
                          style={{
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '14px',
                            transition: 'all 0.3s ease',
                            transform: jdGenerationLoading ? 'scale(0.98)' : 'scale(1)',
                            backgroundColor: 'rgb(89, 144, 225)',
                            borderColor: 'rgb(89, 144, 225)',
                            color: 'white'
                          }}
                        >
                          {jdGenerationLoading ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              <span>Generating...</span>

                            </>
                          ) : (
                            <>
                              <Sparkles size={12} />
                              <span>Generate Job Description</span>

                            </>
                          )}
                        </Button>

                      </Form>
                    </Card.Body>
                  </Card>
                )}

                {/* Enhanced Upload Tab */}
                {activeTab === 'upload' && (
                  <Card className="border-0 shadow-sm" style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#f8f9fa',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Card.Body className="p-3">
                      <div
                        className={`border border-2 border-dashed rounded-2 p-3 text-center transition-all ${dragActive ? 'shadow-sm' : 'border-secondary'
                          }`}
                        style={{
                          background: dragActive ? '#f8f9fa' : '#ffffff',
                          borderColor: dragActive ? 'rgb(89, 144, 225)' : undefined,
                          transition: 'all 0.3s ease',
                          transform: dragActive ? 'scale(1.01)' : 'scale(1)',
                          minHeight: '310px'
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                      >
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2 transition-all"
                          style={{
                            backgroundColor: dragActive ? 'rgba(89, 144, 225, 0.2)' : 'rgba(89, 144, 225, 0.1)',
                            width: '50px',
                            height: '50px'
                          }}>
                          <Upload size={24} style={{ color: 'rgb(89, 144, 225)' }} />
                        </div>

                        {selectedFile ? (
                          <div>
                            <div className="d-inline-flex align-items-center gap-3 p-4 rounded-3 shadow-sm"
                              style={{
                                backgroundColor: 'rgba(89, 144, 225, 0.1)',
                                border: '1px solid rgb(89, 144, 225)'
                              }}>
                              <div className="rounded-circle p-2"
                                style={{ backgroundColor: 'rgba(89, 144, 225, 0.2)' }}>
                                <FileText size={24} style={{ color: 'rgb(89, 144, 225)' }} />
                              </div>
                              <div className="text-start">
                                <p className="fw-bold mb-1" style={{ color: 'rgb(89, 144, 225)' }}>{selectedFile.name}</p>
                                <small className="d-flex align-items-center gap-1" style={{ color: 'rgb(89, 144, 225)' }}>
                                  <CheckCircle2 size={12} />
                                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • Ready to upload
                                </small>
                              </div>
                            </div>
                            <Button
                              variant="link"
                              onClick={() => setSelectedFile(null)}
                              className="text-danger p-0 mt-3 d-flex align-items-center gap-1"
                            >
                              <AlertCircle size={14} />
                              Remove file
                            </Button>
                          </div>
                        ) : (
                          <>
                            <h5 className="fw-bold mb-1 text-dark" style={{ fontSize: '12px' }}>
                              {dragActive ? 'Drop your file here!' : 'Drop your file here'}
                            </h5>
                            <p className="text-muted mb-2" style={{ fontSize: '10px' }}>
                              {dragActive ? 'Release to upload' : 'or click to browse files'}
                            </p>
                            <label className="btn btn-success d-inline-flex align-items-center gap-1 px-3 py-2"
                              style={{ borderRadius: '6px', fontWeight: '600', fontSize: '12px' }}>
                              <Upload size={12} />
                              Choose File
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleFileChange}
                                className="d-none"
                              />
                            </label>
                            <div className="mt-2 p-2 bg-light rounded-2">
                              <div className="d-flex align-items-center justify-content-center gap-1 mb-1">
                                <FileText className="text-muted" size={12} />
                                <span className="fw-semibold text-muted" style={{ fontSize: '10px' }}>Supported Formats</span>
                              </div>
                              <div className="d-flex justify-content-center gap-2">
                                <Badge bg="secondary" className="px-1 py-1" style={{ fontSize: '8px' }}>PDF</Badge>
                                <Badge bg="secondary" className="px-1 py-1" style={{ fontSize: '8px' }}>DOC</Badge>
                                <Badge bg="secondary" className="px-1 py-1" style={{ fontSize: '8px' }}>DOCX</Badge>
                                <Badge bg="secondary" className="px-1 py-1" style={{ fontSize: '8px' }}>TXT</Badge>
                              </div>
                              <small className="text-muted mt-1 d-block" style={{ fontSize: '8px' }}>Maximum file size: 200MB</small>
                            </div>
                          </>
                        )}
                      </div>

                      {selectedFile && (
                        <Button
                          onClick={handleUploadSubmit}
                          disabled={uploadLoading}
                          className="w-100 mt-2 d-flex align-items-center justify-content-center gap-1 py-2"
                          style={{
                            backgroundColor: 'rgb(89, 144, 225)',
                            borderColor: 'rgb(89, 144, 225)',
                            color: 'white',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '12px',
                            transition: 'all 0.3s ease',
                            transform: uploadLoading ? 'scale(0.98)' : 'scale(1)'
                          }}
                        >
                          {uploadLoading ? (
                            <>
                              <Spinner animation="border" size="sm" />
                              <span>Uploading Job Description...</span>
                              <Badge bg="light" text="dark" className="ms-1" style={{ fontSize: '8px' }}>
                                <Clock size={8} className="me-1" />
                                Processing
                              </Badge>
                            </>
                          ) : (
                            <>
                              <Upload size={12} />
                              <span>Upload Job Description</span>
                              <Badge bg="light" text="dark" className="ms-1" style={{ fontSize: '8px' }}>
                                <FileText size={8} className="me-1" />
                                {selectedFile.name.split('.').pop().toUpperCase()}
                              </Badge>
                            </>
                          )}
                        </Button>
                      )}
                    </Card.Body>
                  </Card>
                )}

                {/* Ceipal Import Tab */}
                {activeTab === 'ceipal' && positionCreationSource !== 'manual' && (
                  <Card className="border-0 shadow-sm" style={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#f8f9fa',
                    border: '1px solid rgba(0,0,0,0.05)'
                  }}>
                    <Card.Body className="p-3">
                      <div className="text-center p-4">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                          style={{
                            width: '60px',
                            height: '60px',
                            backgroundColor: 'rgba(89, 144, 225, 0.1)'
                          }}>
                          <Database size={30} style={{ color: 'rgb(89, 144, 225)' }} />
                        </div>

                        <h5 className="fw-bold text-dark mb-2" style={{ fontSize: '16px' }}>
                          Import Job from Ceipal
                        </h5>
                        <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                          Select a job from Ceipal to automatically populate position details and generate JD
                        </p>

                        {selectedCeipalJob ? (
                          <div className="p-3 bg-success bg-opacity-10 border border-success rounded-3 mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <CheckCircle2 className="text-success" size={16} />
                              <span className="fw-bold text-success" style={{ fontSize: '14px' }}>Job Selected</span>
                            </div>
                            <div className="text-start">
                              <p className="fw-bold text-dark mb-1" style={{ fontSize: '14px' }}>
                                {selectedCeipalJob.position_title}
                              </p>
                              <small className="text-muted d-flex align-items-center gap-2">
                                <Badge bg="primary" className="px-2 py-1" style={{ fontSize: '10px' }}>
                                  {selectedCeipalJob.job_code}
                                </Badge>
                                <span>{selectedCeipalJob.city || selectedCeipalJob.country}</span>
                              </small>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={handleImportFromCeipal}
                            disabled={ceipalLoading}
                            className="btn-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2"
                            style={{
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '14px',
                              transition: 'all 0.3s ease',
                              transform: ceipalLoading ? 'scale(0.98)' : 'scale(1)'
                            }}
                          >
                            {ceipalLoading ? (
                              <>
                                <Spinner animation="border" size="sm" />
                                <span>Loading Jobs...</span>
                              </>
                            ) : (
                              <>
                                <Database size={16} />
                                <span>Browse Ceipal Jobs</span>
                              </>
                            )}
                          </Button>
                        )}

                        {selectedCeipalJob && (
                          <div className="d-flex gap-2 justify-content-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setSelectedCeipalJob(null)}
                              className="d-flex align-items-center gap-1"
                              style={{ fontSize: '12px' }}
                            >
                              <AlertCircle size={12} />
                              Change Job
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleImportCeipalJd(selectedCeipalJob)}
                              disabled={jdImportLoading}
                              className="d-flex align-items-center gap-1"
                              style={{ fontSize: '12px' }}
                            >
                              {jdImportLoading ? (
                                <>
                                  <Spinner animation="border" size="sm" />
                                  <span>Importing...</span>
                                </>
                              ) : (
                                <>
                                  <Download size={12} />
                                  <span>Import JD</span>
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                )}

              </div>
            ) : (generatedJd || uploadedJd) ? (
              /* Enhanced Step 3: JD Preview Box with full functionality */
              false ? null : (
                <Card className="border-0 shadow-lg" style={{
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: '#f8f9fa',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <div className="card-header" style={{
                    background: jdType === 'ai' ? '#8e9aaf' : jdType === 'ceipal' ? '#8e9aaf' : jdType === 'upload' ? '#8e9aaf' : '#f8f9fa',
                    border: 'none',
                    padding: '0.75rem 0.5rem'
                  }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                          style={{ width: '40px', height: '40px' }}>
                          <FileText className="text-primary" size={20} />
                        </div>
                        <div>
                          <h5 className="mb-1 text-white fw-bold" style={{ fontSize: '18px' }}>
                            {jdType === 'ai' ? 'AI Generated JD Preview' : jdType === 'ceipal' ? 'Imported JD from Ceipal' : 'Uploaded JD Preview'}
                          </h5>
                          <div className="d-flex align-items-center gap-2">
                            <CheckCircle2 size={10} className="text-white" />
                            <small className="text-white" style={{ fontSize: '9px', opacity: '0.8' }}>
                              Ready to review
                            </small>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-1 flex-wrap">
                        {jdType !== 'ceipal' && (
                        <Button
                          variant="light"
                          size="sm"
                          className="d-flex align-items-center gap-1"
                          style={{
                            fontSize: '10px',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: 'white',
                            borderColor: 'rgb(89, 144, 225)',
                            color: 'rgb(89, 144, 225)',
                            borderWidth: '1px'
                          }}
                          onClick={() => {
                            setShowJdPreview(false);
                            // Clear comparison state so "Back" truly returns to step 2 UI
                            setOriginalJd('');
                            setRegeneratedJd('');
                            setShowComparison(false);
                            setShowDiff(false);
                          }}
                        >
                          <ArrowRight size={10} style={{ transform: 'rotate(180deg)' }} />
                          <span>Back</span>
                        </Button>
                        )}
                        {aiJdOn && (generatedJd || uploadedJd) && (
                          <Button
                            variant="light"
                            size="sm"
                            className="d-flex align-items-center gap-1"
                            style={{
                              fontSize: '10px',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'white',
                              borderColor: 'rgb(89, 144, 225)',
                              color: 'rgb(89, 144, 225)',
                              borderWidth: '1px'
                            }}
                            onClick={handleRegenerateCurrentJd}
                            disabled={jdGenerationLoading}
                          >
                            {jdGenerationLoading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <Sparkles size={10} />
                            )}
                            Regenerate JD
                          </Button>
                        )}
                        <Button
                          variant="outline-light"
                          size="sm"
                          className="d-flex align-items-center gap-1 px-2 py-1"
                          style={{
                            borderRadius: '6px',
                            fontWeight: '600',
                            borderWidth: '1px',
                            fontSize: '10px'
                          }}
                          onClick={() => {
                            // In comparison mode use the selected version; otherwise fall back to the current JD.
                            const jdContentRaw = showComparison && selectedJdForAttach
                              ? (selectedJdForAttach === 'regenerated' ? regeneratedJd : originalJd)
                              : (jdType === 'upload' ? uploadedJd : generatedJd);
                            const jdContent = jdContentRaw;
                            navigate(`/jd-preview/${positionId}`, {
                              state: buildJdPreviewNavigationState({
                                returnTo: {
                                  pathname: '/open-positions',
                                  state: {
                                    fromJdPreview: true,
                                    positionId,
                                    positionName,
                                    clientName,
                                    jobCode,
                                    jdType,
                                    jdContent,
                                    // Preserve comparison state so Back restores the selection view.
                                    showComparison,
                                    originalJd: showComparison ? originalJd : undefined,
                                    regeneratedJd: showComparison ? regeneratedJd : undefined,
                                    selectedJdForAttach: showComparison ? selectedJdForAttach : undefined,
                                    selectedCeipalJob: jdType === 'ceipal' ? selectedCeipalJob : undefined,
                                    structuredData: jdType === 'ceipal' ? structuredData : undefined,
                                  },
                                },
                                jdContent,
                                jdType,
                                positionName,
                                clientName,
                                fromOpenPositions: true,
                              }),
                            });
                          }}
                        >
                          <Edit3 size={10} />
                          <span>Edit/Download</span>
                        </Button>
                        {/* Attach JD button — only active once user has selected a JD (or no comparison shown) */}
                        <Button
                          variant="light"
                          size="sm"
                          className="d-flex align-items-center gap-1"
                          style={{
                            fontSize: '10px',
                            padding: '0.25rem 0.6rem',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: '6px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            ...((!showComparison || selectedJdForAttach) && !jdAttached
                              ? {
                                  backgroundColor: '#198754',
                                  borderColor: '#198754',
                                  color: 'white',
                                  cursor: 'pointer',
                                }
                              : jdAttached
                              ? {
                                  backgroundColor: 'white',
                                  borderColor: '#198754',
                                  color: '#198754',
                                  cursor: 'default',
                                  opacity: 0.8,
                                }
                              : {
                                  backgroundColor: '#e9ecef',
                                  borderColor: '#ced4da',
                                  color: '#adb5bd',
                                  cursor: 'not-allowed',
                                })
                          }}
                          onClick={() => {
                            if (jdAttached) return;
                            if (showComparison && !selectedJdForAttach) return;
                            handleAttachJd();
                          }}
                          disabled={attachingJd || (showComparison && !selectedJdForAttach)}
                          title={
                            jdAttached
                              ? 'JD already attached'
                              : showComparison && !selectedJdForAttach
                              ? 'Select a JD version first'
                              : 'Attach this JD to the position'
                          }
                        >
                          {attachingJd ? (
                            <Spinner animation="border" size="sm" />
                          ) : jdAttached ? (
                            <>
                              <CheckCircle2 size={10} />
                              <span>JD Attached</span>
                            </>
                          ) : (
                            <>
                              <Paperclip size={10} />
                              <span>Attach JD</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <Card.Body className="p-3">
                    <div className="mb-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="fw-bold text-dark mb-0" style={{ fontSize: '14px' }}>
                          Job Description Preview
                        </h6>
                        <div className="d-flex align-items-center gap-1">

                          <small className="text-muted" style={{ fontSize: '9px' }}>
                            {jdType === 'ai' ? 'Generated with AI' : jdType === 'ceipal' ? 'Imported from Ceipal' : 'From file upload'}
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* JD Selection view after regeneration — hover to preview, click to select */}
                    {showComparison && (jdType === 'upload' || jdType === 'ceipal' || jdType === 'ai') && originalJd && regeneratedJd ? (
                      <div>
                        <style>{JD_PREVIEW_INNER_CSS}</style>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <small className="fw-semibold text-dark" style={{ fontSize: '12px' }}>
                            Select which JD to attach:
                          </small>
                          <small className="text-muted" style={{ fontSize: '10px' }}>
                            Hover to preview • Click to select
                          </small>
                        </div>
                        <Row className="g-3">
                          {[
                            { key: 'original', label: 'Original JD', badge: 'Before', badgeColor: '#6c757d', content: originalJd },
                            { key: 'regenerated', label: 'Regenerated JD', badge: 'AI Updated', badgeColor: 'rgb(89,144,225)', content: regeneratedJd },
                          ].map(({ key, label, badge, badgeColor, content }) => {
                            const isSelected = selectedJdForAttach === key;
                            const toPreviewHtml = (v) => formatJdPreviewHtml(v);
                            return (
                              <Col md={6} key={key}>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setSelectedJdForAttach(key)}
                                  onKeyDown={(e) => e.key === 'Enter' && setSelectedJdForAttach(key)}
                                  className="jd-select-card"
                                  style={{
                                    position: 'relative',
                                    borderRadius: '10px',
                                    border: isSelected
                                      ? '2px solid #198754'
                                      : '2px solid #dee2e6',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: isSelected
                                      ? '0 0 0 3px rgba(25,135,84,0.15)'
                                      : 'none',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = badgeColor;
                                      e.currentTarget.style.boxShadow = `0 4px 20px rgba(0,0,0,0.12)`;
                                    }
                                    const bubble = e.currentTarget.querySelector('.jd-hover-bubble');
                                    if (bubble) bubble.style.opacity = '1';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.borderColor = '#dee2e6';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }
                                    const bubble = e.currentTarget.querySelector('.jd-hover-bubble');
                                    if (bubble) bubble.style.opacity = '0';
                                  }}
                                >
                                  {/* Floating bubble overlay on hover */}
                                  <div
                                    className="jd-hover-bubble"
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      backgroundColor: isSelected ? '#198754' : badgeColor,
                                      color: 'white',
                                      borderRadius: '50px',
                                      padding: '8px 18px',
                                      fontWeight: '700',
                                      fontSize: '13px',
                                      opacity: isSelected ? '1' : '0',
                                      transition: 'opacity 0.15s ease',
                                      pointerEvents: 'none',
                                      zIndex: 10,
                                      whiteSpace: 'nowrap',
                                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                    }}
                                  >
                                    {isSelected ? '✓ Selected' : `Use ${label}`}
                                  </div>

                                  {/* Card header */}
                                  <div style={{
                                    background: isSelected ? '#198754' : '#f8f9fa',
                                    padding: '8px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #e9ecef',
                                    transition: 'background 0.2s ease'
                                  }}>
                                    <span className="fw-bold" style={{
                                      fontSize: '12px',
                                      color: isSelected ? 'white' : '#2c3e50'
                                    }}>{label}</span>
                                    <span style={{
                                      fontSize: '9px',
                                      fontWeight: '600',
                                      padding: '2px 8px',
                                      borderRadius: '10px',
                                      backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : badgeColor,
                                      color: 'white'
                                    }}>{badge}</span>
                                  </div>

                                  {/* JD content */}
                                  <div
                                    className="jd-preview-content"
                                    style={{
                                      maxHeight: '55vh',
                                      overflowY: 'auto',
                                      overflowX: 'hidden',
                                      fontSize: '0.72rem',
                                      lineHeight: '1.4',
                                      padding: '10px 12px',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere',
                                      backgroundColor: isSelected ? '#f0fdf4' : '#ffffff',
                                      transition: 'background 0.2s ease',
                                      textAlign: 'left',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: toPreviewHtml(content) }}
                                  />
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                        {!selectedJdForAttach && (
                          <div className="text-center mt-3">
                            <small className="text-muted" style={{ fontSize: '11px' }}>
                              Click a JD above to select it, then click <strong>Attach JD</strong> to confirm
                            </small>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {/* Hide the normal preview when comparison is shown (AI/Upload/Ceipal), so the compare stays in-place */}
                    {!(showComparison && (jdType === 'upload' || jdType === 'ceipal' || jdType === 'ai') && originalJd && regeneratedJd) && (
                      <>
                        <style>{JD_PREVIEW_INNER_CSS}</style>
                        <div
                          className="jd-content jd-preview-content"
                          style={{
                            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                            fontSize: '0.75rem',
                            lineHeight: '1.4',
                            padding: '12px',
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                            minHeight: '500px',
                            maxHeight: '100vh',
                            overflowY: 'auto',
                            color: '#2c3e50',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
                            textAlign: 'left',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatJdPreviewHtml(
                              jdType === 'upload' ? uploadedJd : generatedJd
                            ),
                          }}
                        />
                      </>
                    )}

                    <div className="mt-2 d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-1">
                        <small className="text-muted" style={{ fontSize: '9px' }}>
                          <FileText size={10} className="me-1" />
                          {jdType === 'ai' ? 'AI-generated content' : jdType === 'ceipal' ? 'Imported from Ceipal' : 'Uploaded content'}
                        </small>
                      </div>
                      {/* <div className="d-flex gap-1">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  className="d-flex align-items-center gap-1"
                                  style={{fontSize: '10px', padding: '0.25rem 0.5rem'}}
                                  onClick={() => {
                                    const jdContent = jdType === 'ai' ? generatedJd : uploadedJd;
                                    const blob = new Blob([jdContent], { type: 'text/plain' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `${positionName || 'job-description'}.txt`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  }}
                                >
                                  <Download size={10} />
                                  Download
                                </Button>
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  className="d-flex align-items-center gap-1"
                                  style={{fontSize: '10px', padding: '0.25rem 0.5rem'}}
                                  onClick={() => {
                                    const jdContent = jdType === 'ai' ? generatedJd : uploadedJd;
                                    navigate(`/jd-preview/${positionId}`, {
                                      state: {
                                        jdContent: jdContent,
                                        jdType: jdType,
                                        positionName: positionName,
                                        clientName: clientName,
                                        fromOpenPositions: true
                                      }
                                    });
                                  }}
                                >
                                  <Edit3 size={10} />
                                  Edit
                                </Button>
                              </div> */}
                    </div>
                  </Card.Body>
                </Card>
              )
            )
              : null
            }
          </Col>
        </Row>
      </Container>

      {/* Ceipal Job Selection Modal */}
      <Modal show={showCeipalModal} onHide={() => setShowCeipalModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#8e9aaf', color: 'white' }}>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Database size={20} />
            Select Job from Ceipal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
            <div className="flex-grow-1 position-relative" style={{ minWidth: '200px' }}>
              <Form.Control
                type="text"
                placeholder="Search by job title or job code..."
                value={ceipalSearchTerm}
                onChange={(e) => {
                  setCeipalSearchTerm(e.target.value);
                  setCeipalCurrentPage(1); // Instant page reset so results show on page 1 immediately
                }}
                className="ps-3"
                style={{ borderRadius: '8px' }}
              />
            </div>
            {/* Filter toggle: All / New / Used */}
            <div className="d-flex gap-1" style={{ flexShrink: 0 }}>
              {[
                { key: 'all',  label: 'All' },
                { key: 'new',  label: 'Available' },
                { key: 'used', label: 'Already Used' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCeipalJobFilter(key)}
                  style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    padding: '5px 12px',
                    borderRadius: '20px',
                    border: ceipalJobFilter === key ? 'none' : '1px solid #dee2e6',
                    cursor: 'pointer',
                    backgroundColor: ceipalJobFilter === key
                      ? (key === 'used' ? '#dc3545' : key === 'new' ? '#198754' : 'rgb(89,144,225)')
                      : '#f8f9fa',
                    color: ceipalJobFilter === key ? 'white' : '#6c757d',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {usedJobCodesLoading && (
              <div className="d-flex align-items-center gap-2 mb-2 px-2 py-1" style={{ backgroundColor: '#fff8e1', borderRadius: '6px', border: '1px solid #ffe082' }}>
                <div className="spinner-border spinner-border-sm" role="status" style={{ color: '#f59e0b' }} aria-hidden="true" />
                <small style={{ color: '#92400e', fontWeight: 500 }}>Checking which positions are already created...</small>
              </div>
            )}
            {ceipalSearchTerm.trim() && ceipalFullSearchLoading && (
              <div className="d-flex align-items-center justify-content-end mb-2 pe-2">
                <div className="spinner-border spinner-border-sm text-secondary me-2" role="status" aria-hidden="true" />
                <small className="text-muted">Searching all job codes...</small>
              </div>
            )}
            {ceipalLoading && (
              <div className="d-flex align-items-center justify-content-end mb-2 pe-2">
                <div className="spinner-border spinner-border-sm text-secondary me-2" role="status" aria-hidden="true" />
                <small className="text-muted">Loading page...</small>
              </div>
            )}
            <Table responsive hover>
              <thead className="bg-light sticky-top">
                <tr>
                  <th>Job Code</th>
                  <th>Position Title</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filterAwareDisplayedJobs.map((job, index) => {
                  const isUsed = usedJobCodes.has((job.job_code || '').toLowerCase().trim());
                  return (
                    <tr
                      key={job.id || index}
                      style={{
                        opacity: isUsed ? 0.45 : 1,
                        backgroundColor: isUsed ? '#f8f9fa' : undefined,
                        pointerEvents: isUsed ? 'none' : undefined,
                      }}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-1 flex-wrap">
                          <Badge className="px-2 py-1" style={{
                            backgroundColor: isUsed ? '#adb5bd' : 'rgb(89, 144, 225)',
                            color: 'white',
                            fontSize: '8px',
                            padding: '2px 6px',
                            border: 'none',
                          }}>
                            {job.job_code}
                          </Badge>
                          {isUsed && (
                            <Badge style={{
                              fontSize: '7px',
                              padding: '2px 6px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontWeight: 600,
                            }}>
                              Already Used
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-bold" style={{ fontSize: '14px' }}>
                            {job.position_title}
                          </div>
                          {job.skills && (
                            <small className="text-muted">
                              Skills: {job.skills.split(',').slice(0, 2).join(', ')}
                              {job.skills.split(',').length > 2 && '...'}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <MapPin size={12} className="text-muted" />
                          <span style={{ fontSize: '12px' }}>
                            {job.city || job.country || 'Not specified'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={job.job_status === 'Active' ? 'success' : 'secondary'}
                          className="px-2 py-1"
                          style={{
                            fontSize: '8px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            border: 'none',
                          }}
                        >
                          {job.job_status}
                        </Badge>
                      </td>
                      <td>
                        {isUsed ? (
                          <span style={{ fontSize: '11px', color: '#adb5bd', fontStyle: 'italic' }}>
                            Position exists
                          </span>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectCeipalJob(job)}
                            className="d-flex align-items-center gap-1"
                            style={{
                              fontSize: '12px',
                              borderColor: 'rgb(89, 144, 225)',
                              color: 'rgb(89, 144, 225)',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgb(89, 144, 225)';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = 'rgb(89, 144, 225)';
                            }}
                          >
                            <Check size={12} />
                            Select
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {filterAwareDisplayedJobs.length === 0 && (
              <div className="text-center py-4">
                <Database size={48} className="text-muted mb-2" />
                <p className="text-muted mb-0">
                  {ceipalJobFilter === 'new'
                    ? 'No available (unused) jobs found on this page'
                    : ceipalJobFilter === 'used'
                    ? 'No already-used jobs found on this page'
                    : 'No jobs found matching your search criteria'}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {effectiveCeipalTotalPages > 1 && (
            <div className="mt-3 d-flex justify-content-center">
              <GlobalPagination
                currentPage={ceipalCurrentPage}
                totalPages={effectiveCeipalTotalPages}
                onPageChange={handleCeipalPageChange}
                recordsPerPage={ceipalRecordsPerPage}
                onPageSizeChange={handleCeipalPageSizeChange}
                totalItems={effectiveCeipalTotalItems}
                startIndex={ceipalStartIndex}
                endIndex={ceipalEndIndex}
                label="Ceipal jobs"
                size="medium"
                isLoading={ceipalLoading}
              />
            </div>
          )}

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCeipalModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>

    {/* Attach JD Success Modal */}
    <Modal
      show={showAttachSuccessModal}
      onHide={() => {}}
      onExited={() => {
        setAttachedPositionForRanking(null);
        setView('list');
      }}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header style={{ backgroundColor: '#198754', color: 'white', borderBottom: 'none' }}>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '16px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          JD Attached Successfully!
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
        <p style={{ fontSize: '15px', color: '#374151', marginBottom: '4px' }}>
          The Job Description has been saved to <strong>{positionName || 'the position'}</strong>.
        </p>
        <p style={{ fontSize: '13px', color: '#6b7280' }}>
          Next, review the sector, domain, role profile and skill weightage for this position.
        </p>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', justifyContent: 'center', gap: 8 }}>
        <Button
          variant="outline-secondary"
          style={{ minWidth: '120px', fontWeight: '600' }}
          onClick={() => setShowAttachSuccessModal(false)}
        >
          Back to Positions
        </Button>
        <Button
          variant="success"
          style={{ minWidth: '180px', fontWeight: '600' }}
          onClick={() => {
            const targetId = attachedPositionForRanking || positionId;
            setShowAttachSuccessModal(false);
            if (targetId) {
              navigate(`/stack-ranking/${targetId}`, { state: { _refresh: Date.now() } });
            }
          }}
        >
          Configure Skill Weightage
        </Button>
      </Modal.Footer>
    </Modal>

    {jobTitleLimitModal}
    </>
  );
};

export default OpenPositions;